import type {
  AccountsUsageInfo,
  BillingCancelOrdwaySubscriptionRequest,
  BillingCancelRequest,
  BillingGetInfoResponse,
  BillingInfo,
  BillingMetronomeProfileResponse,
  BillingOrdwayCouponModel,
  BillingOrdwayCustomerModel,
  BillingOrdwayProfileResponse,
  BillingOrdwaySubscriptionModel,
  BillingOrdwaySubscriptionRequest,
  BillingPauseRequest,
  BillingUpdateOrdwayProfileRequest,
  Coupon,
  TaxRate,
  Tier,
} from '@customerio/hydra-ui-api';
import { announceRefreshAuth } from '@customerio/hydra-nav-state';
import plur from 'plur';
import { atom, computed } from 'nanostores';
import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import { formatRelativeTime } from '@customerio/pluma-components/react/utils/formatted-relative-time';
import { hydraUiApi } from '@/services/api';
import { parseDateLoosely } from '@/utils/time';
import { AccountState, VerificationState, type AugmentedAccount } from '@/services/auth/types';
import { accountStore, userStore } from '@/services/auth/store';
import { FLEX_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { accountHasFeatureFlag, AccountFeatureFlag } from '@/services/auth/feature-flags';
import { masqReadOnlyStore } from '@/utils/masq';
import type { AccountPermission } from '@customerio/hydra-permissions';
import { applyMasqReadOnly, checkPermission } from '@customerio/hydra-permissions/validation';
import { BillingSystem, type BillingEstimates, type PaymentCard, type SetupIntentPayload } from './types';
import { resolveBillingVendor } from './resolve-vendor';
import { resolveUsageSource, usageCacheKey, type UsageSource } from './usage-source';
import { stripeCustomerToPaymentCard } from './stripe/card';
import { ordwayPaymentToPaymentCard } from './ordway/card';
import { stripeFormatter } from './stripe/errors';
import { MASQ_READ_ONLY_MESSAGE, NO_BILLING_PERMISSION_MESSAGE, VERIFICATION_REJECTED_TOOLTIP } from './messages';
import { couponActiveForCharge, couponActiveForChargeDate } from './coupon';
import { coalesceTask, enqueueTask } from '@/utils/enqueue-task';
import { pollDelay, pollTask } from '@/utils/poll-task';
import {
  deleteCrossAppCacheByPrefix,
  deleteCrossAppCacheKeys,
  getCrossAppCache,
  setCrossAppCache,
} from '@/utils/cross-app-cache';
import { isEmptyString } from '@/utils/string';
import { snackbarApi } from '@/services/snackbar';
import { sentryCaptureException } from '@/utils/sentry';
import type { ProductApp, StartupType } from './plans';
import { getUpgradePlanTypes, Plans, PlanType } from './plans';
import { baseOrdwayProductIds, ordwayProductIds, ProductName } from './ordway/products';
import { BillingCadence, isNonMonthlyContract, ordwayCadence, stripeCadence } from './cadence';
import {
  ordwayPlanToBillingPlan,
  ordwaySubscriptionToBillingPlan,
  stripePlanToBillingPlan,
  stripeSubscriptionToBillingData,
  metronomeProfileToBillingPlan,
  type OrdwayPlanBillingPlan,
  type OrdwaySubscriptionBillingPlan,
  type StripeBillingPlan,
  type StripeSubscriptionBillingData,
  type StripePlanContext,
  type MetronomeSubscriptionBillingPlan,
  toBillingUsage,
  hasUnbilledEnvironment,
  type BillingUsage,
} from './transforms';
import { type OrdwayCustomerCustomFields } from './ordway/types';
import { PlanChangeTiming, scheduledPlanChange, type ScheduledPlanChange } from './scheduled-plan-change';
import {
  createOrdwayPollTracker,
  ordwayPurchaseStatusError,
  readOrdwayPurchaseStatus,
  reportIndeterminateOrdwayPurchase,
  ORDWAY_AI_CREDITS_RAIL,
  ORDWAY_INBOX_PREVIEWS_RAIL,
  type OrdwayPurchaseRail,
  type OrdwayPurchaseStatusRead,
} from './ordway/credits-purchase';
import { pollForOrdwayCheckoutTask } from './ordway/checkout-status';
import { BILLING_TIME_ZONE } from './time-zone';

/** Feature flag for the "new pipelines objects pricing" plan highlights. */
const NEW_PIPELINES_OBJECTS_PRICING_FLAG = 'new_pipelines_objects_pricing';

/** One product line on an Ordway subscription (`subscription.plans[]`). */
export type OrdwaySubscriptionProduct = NonNullable<BillingOrdwaySubscriptionModel['plans']>[number];

/**
 * The effective subscription on {@link billing.subscription}. An Ordway account holds its Ordway
 * subscription in the shared `BillingPlan` shape. A legacy Stripe account holds the raw Stripe
 * subscription, with `plan` resolved to a `StripeBillingPlan` and a `hasCard` flag added. Both keep
 * the raw wire fields beside the shared ones.
 */
export type BillingSubscription =
  | StripeSubscriptionBillingData
  | OrdwaySubscriptionBillingPlan
  | MetronomeSubscriptionBillingPlan;

export type BillingCoupon = Coupon | BillingOrdwayCouponModel;

/** Options for {@link billing.initialize}. */
export interface InitializeBillingOptions {
  /** Fetch again when the service is initialized. A short throttle applies. */
  refresh?: boolean;
  /** Ignore request errors and set the service initialized. */
  ignoreErrors?: boolean;
  /** Plan slug from a URL query param. Selects {@link billing.paramPlan} and bypasses the throttle. */
  paramPlanSlug?: string;
  includeCoupons?: boolean;
}

/**
 * Refresh throttle (ms). When the service is initialized and a refresh has no coupon or plan param,
 * ignore refreshes in this window. The user is probably only being redirected.
 */
const RECENTLY_INITIALIZED_WINDOW_MS = 500;

const ESSENTIALS_PLAN_SLUG = '202301_pricing_essentials';

const POLL_MAX_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 1000;

/**
 * Status-poll limits shared by every Ordway purchase rail (AI credits, Inbox Previews), which the
 * limits above are far too short for: those endpoints return as soon as the workflow starts, so
 * polling covers the purchase's whole duration — up to a minute in production — rather than a brief
 * settling window. 3 minutes leaves headroom for tail latency, and the slower interval keeps the
 * request count proportionate.
 */
const ORDWAY_PURCHASE_POLL_MAX_ATTEMPTS = 90;
const ORDWAY_PURCHASE_POLL_INTERVAL_MS = 2000;

/**
 * Wait limits for an Ordway reactivation. The endpoint answers as soon as the workflow starts, and the
 * workflow sets the account state, so the account reads as canceled for some time after a success. The
 * purchase limits above cover a wait of the same length.
 */
const REACTIVATION_POLL_MAX_ATTEMPTS = 90;
const REACTIVATION_POLL_INTERVAL_MS = 2000;

/**
 * How many 404s to ride out before calling the run lost. Temporal's describe is consistent, so a 404
 * cannot mean "started but not visible yet" — but a rolling services deploy can answer from an
 * instance that does not serve the status route at all, which is exactly the window this UI ships
 * into. Retrying a few times distinguishes that from a genuinely unknown run.
 */
const ORDWAY_PURCHASE_NOT_FOUND_TOLERANCE = 3;

/**
 * How long a billing response stays usable in the cross-app cache (ms). Each boot of an embedded app
 * would otherwise request the billing endpoints again and cause 429s from the billing vendor. A
 * cached response lets non-refresh `initialize` calls — here, or in an embedded app that shares
 * `window.__hydra_cross_app_cache__` — read a recent response instead of the network.
 */
const BILLING_CACHE_TTL_MS = 30_000;

/**
 * Loads the Ordway billing profile. A non-refresh call reads the cross-app cache first. A cache miss,
 * or `refresh`, goes to the network and writes the response back. The cache key is the same as the
 * embedded app's key, so the two apps share cached responses.
 */
async function loadOrdwayProfile(
  accountId: number,
  includeCoupons: boolean,
  refresh: boolean,
): Promise<BillingOrdwayProfileResponse> {
  const cacheKey = `/accounts/${accountId}/billing/profile?coupons=${includeCoupons}`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return cached as BillingOrdwayProfileResponse;
  }

  const res = await hydraUiApi.billing.getProfile({
    params: { path: { account_id: String(accountId) }, query: { coupons: String(includeCoupons) } },
  });
  setCrossAppCache({ key: cacheKey, value: res, ttl: BILLING_CACHE_TTL_MS });
  return res;
}

/**
 * Loads the legacy Stripe billing info, with the same cache behavior as {@link loadOrdwayProfile}.
 *
 * The key has no query segments, because the request has no query params. It sends only the account id.
 * `paramPlanSlug` never goes to the server. It only selects a plan from the list that the response
 * already contains (see `setStripeBillingProperties`). A key that includes the slug makes one entry for
 * each slug of the same response, so a visit with a plan param fetches again what a plain visit cached.
 *
 * The query params also cannot be wired up later. `GetInfo` binds no query struct, and
 * `getPlans(ctx, tr, account, subscription)` cannot take an extra plan.
 */
async function loadStripeInfo(accountId: number, refresh: boolean): Promise<BillingGetInfoResponse> {
  const cacheKey = `/accounts/${accountId}/billing/info`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return cached as BillingGetInfoResponse;
  }

  const res = await hydraUiApi.billing.getInfo({
    params: { path: { account_id: String(accountId) } },
  });
  setCrossAppCache({ key: cacheKey, value: res, ttl: BILLING_CACHE_TTL_MS });
  return res;
}

async function loadMetronomeProfile(accountId: number, refresh: boolean): Promise<BillingMetronomeProfileResponse> {
  const cacheKey = `/accounts/${accountId}/billing/metronome_profile`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return cached as BillingMetronomeProfileResponse;
  }

  const res = await hydraUiApi.billing.getMetronomeProfile({
    params: { path: { account_id: String(accountId) } },
  });
  setCrossAppCache({ key: cacheKey, value: res, ttl: BILLING_CACHE_TTL_MS });
  return res;
}

/** Requests usage from the endpoint {@link resolveUsageSource} picked. Both share a response shape. */
async function fetchUsage(accountId: number, source: UsageSource) {
  if (source.endpoint === 'usage_v2') {
    return hydraUiApi.accounts.getUsageV2({
      params: { path: { account_id: String(accountId) }, query: { start: source.start } },
    });
  }

  return hydraUiApi.accounts.getUsage({ params: { path: { account_id: String(accountId) } } });
}

/**
 * Loads the account usage, with the same cache behavior as {@link loadStripeInfo}. Callers get it on
 * demand through `billing.loadUsage`, because the billing service is the one gateway for usage.
 *
 * An Ordway subscription reads `usage_v2` — see {@link resolveUsageSource}.
 */
async function loadAccountUsage(accountId: number, refresh: boolean): Promise<BillingUsage | undefined> {
  // The endpoint choice reads the subscription, which only `initialize` sets — and the layout starts it
  // without awaiting. Without this, a page mounting early takes the legacy endpoint and reports no MAU.
  // A separate queue from `loadUsage`, so this cannot deadlock.
  await billing.initialize.perform({ ignoreErrors: true });

  const source = resolveUsageSource({
    isOrdwaySubscription: isOrdwaySubscription(subscription.get()),
    billingPeriodStart: billingPeriodStart.get(),
  });

  const cacheKey = usageCacheKey(accountId, source);
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  // The cache holds the wire shape, because the key names a server endpoint and embedded apps share it.
  if (cached != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return toBillingUsage(cached as AccountsUsageInfo);
  }

  const res = await fetchUsage(accountId, source);
  const usage = res.account_usage ?? undefined;
  if (usage == null) {
    return undefined;
  }

  setCrossAppCache({ key: cacheKey, value: usage, ttl: BILLING_CACHE_TTL_MS });
  return toBillingUsage(usage);
}

/**
 * Fetches the Ordway overage estimate. The endpoint returns dollar strings, but the shared
 * {@link BillingEstimates} shape is integer cents. `object_overage` and `cdp_api_call_overage` are not
 * on the wire, so they are always 0. Only for non-trial Ordway accounts with a subscription (see
 * {@link billing.fetchOrdwayBillingEstimate}).
 */
async function loadOrdwayBillingEstimate(accountId: number, subscriptionId: string): Promise<BillingEstimates> {
  const res = await hydraUiApi.billing.getOverageEstimate({
    params: { path: { account_id: String(accountId) }, query: { subscription_id: subscriptionId } },
  });

  // An empty or absent field (non-Ordway, or no schedule) becomes 0, not NaN.
  const toCents = (value?: string): number => {
    const parsed = Number.parseFloat(value ?? '');
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return Math.round(parsed * 100);
  };

  return {
    invoice_estimate: toCents(res.invoice_estimate),
    customer_overage: toCents(res.customer_overage),
    message_overage: toCents(res.message_overage),
    mau_overage: toCents(res.mau_overage),
    object_overage: 0,
    cdp_api_call_overage: 0,
  };
}

/** Sorts Ordway subscriptions by service start date, oldest first. A subscription with no date is last. */
function sortOrdwaySubscriptionsByDate(
  subscriptions: readonly BillingOrdwaySubscriptionModel[],
): BillingOrdwaySubscriptionModel[] {
  return [...subscriptions].sort((a, b) => {
    const aTime = isEmptyString(a.service_start_date) ? Infinity : Date.parse(a.service_start_date ?? '');
    const bTime = isEmptyString(b.service_start_date) ? Infinity : Date.parse(b.service_start_date ?? '');
    return aTime - bTime;
  });
}

/** Which Ordway subscription is in force and which the account moves onto next. */
type OrdwaySubscriptionPair = {
  effective: BillingOrdwaySubscriptionModel | undefined;
  upcoming: BillingOrdwaySubscriptionModel | undefined;
};

/**
 * The effective and upcoming subscriptions on an Ordway profile response.
 *
 * The oldest by service start date is the one in force; the next is what the
 * account moves onto. Only the upcoming one is deduped — the backend filters
 * the effective subscription's products but not a future one's.
 */
function resolveOrdwaySubscriptions(subscriptions: readonly BillingOrdwaySubscriptionModel[]): OrdwaySubscriptionPair {
  const sorted = sortOrdwaySubscriptionsByDate(subscriptions);
  const upcoming = sorted[1];

  return {
    effective: sorted[0],
    upcoming:
      upcoming == null ? undefined : { ...upcoming, plans: dedupeFutureSubscriptionProducts(upcoming.plans ?? []) },
  };
}

/**
 * Collapses duplicate `product_id`s. The backend filters the products of the effective subscription,
 * but not those of a future subscription. A product with no charge start date is aligned with the
 * service start date and wins. If both products have a charge start date, the earliest wins.
 */
function dedupeFutureSubscriptionProducts(products: readonly OrdwaySubscriptionProduct[]): OrdwaySubscriptionProduct[] {
  const productById: Record<string, OrdwaySubscriptionProduct> = {};

  for (const product of products) {
    const existing = productById[product.product_id];

    if (existing == null) {
      productById[product.product_id] = product;
      continue;
    }

    if (isEmptyString(product.charge_start_date)) {
      productById[product.product_id] = product;
    } else if (!isEmptyString(existing.charge_start_date)) {
      if (Date.parse(product.charge_start_date ?? '') < Date.parse(existing.charge_start_date ?? '')) {
        productById[product.product_id] = product;
      }
    }
  }

  return Object.values(productById);
}

/**
 * Builds the {@link StripePlanContext} for the Stripe plan transform. The values are read once from the
 * billing info and the account at load time, which keeps the transform pure. A trial is
 * `premium_trial || basic_trial`, or the account's `suspend_trial_at` when there is no billing info.
 */
// fallow-ignore-next-line complexity -- inherent to the billing domain mapping
function stripePlanContext(billingInfo: BillingInfo | undefined): StripePlanContext {
  const account = accountStore.get();
  const isAccountTrial =
    billingInfo == null ? account?.suspend_trial_at != null : billingInfo.basic_trial || billingInfo.premium_trial;

  return {
    cancellable: billingInfo?.cancellable ?? false,
    isAccountTrial,
    startupProgram: isEmptyString(billingInfo?.startup_program)
      ? undefined
      : // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        (billingInfo?.startup_program as StartupType),
    newPipelinesObjectsPricing: account?.feature_flags?.[NEW_PIPELINES_OBJECTS_PRICING_FLAG] ?? false,
  };
}

/** The Stripe subscription's saved card as a {@link PaymentCard}, or null when there is no card. */
function stripeSubscriptionCard(subscription: BillingGetInfoResponse['subscription']): PaymentCard | null {
  const customer = subscription?.stripe_customer;
  // `hasCard` is derived from an empty `card_type`. Use the same test, so `cardDetails` is null when
  // `hasCard` is false, and not an empty card.
  if (customer == null || isEmptyString(customer.card_type)) {
    return null;
  }
  return stripeCustomerToPaymentCard(customer);
}

// The subscription atom holds a Stripe *or* an Ordway subscription with a `billingSystem`
// discriminant, so the union narrows on that field.
function isOrdwaySubscription(
  subscription: BillingSubscription | undefined,
): subscription is OrdwaySubscriptionBillingPlan {
  return subscription?.billingSystem === BillingSystem.Ordway;
}

function isStripeSubscription(
  subscription: BillingSubscription | undefined,
): subscription is StripeSubscriptionBillingData {
  return subscription?.billingSystem === BillingSystem.Stripe;
}

function isMetronomeSubscription(
  subscription: BillingSubscription | undefined,
): subscription is MetronomeSubscriptionBillingPlan {
  return subscription?.billingSystem === BillingSystem.Metronome;
}

function positiveOrUndefined(value: number | null | undefined): number | undefined {
  return value != null && value > 0 ? value : undefined;
}

function isOrdwayTrialSuspended(trialExpired: boolean): boolean {
  const state = accountStore.get()?.account_state;
  const isSuspendedOrCanceled = state === AccountState.Suspended || state === AccountState.Canceled;
  return isSuspendedOrCanceled && (subscription.get() == null || trialExpired);
}

function isStripeTrialExpired(account: ReturnType<typeof accountStore.get>): boolean {
  const suspendTrialAt = account?.suspend_trial_at;
  const suspendedInPast = suspendTrialAt != null && suspendTrialAt * 1000 < Date.now();
  return suspendedInPast || account?.planFeatures.messaging.expired === true;
}

// Ordway sends calendar dates, so zero the time in UTC before the change to unix seconds.
function utcStartOfDayUnix(date: string): number {
  const parsed = new Date(date);
  return Math.floor(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()) / 1000);
}

// Like the helper above, but for a date that may not parse. `parseDateLoosely` reads a date-only
// string as a calendar date with no time zone, then `toDate` anchors it to midnight in the billing
// time zone, where it is shown. An
// unparsable date gives `undefined` and does not throw, because this feeds a nanostores computed,
// where a throw corrupts the shared notify queue for all other computeds.
function looseBillingStartOfDayUnix(date: string): number | undefined {
  const parsed = parseDateLoosely(date);
  return parsed == null ? undefined : Math.floor(parsed.toDate(BILLING_TIME_ZONE).getTime() / 1000);
}

// The last second of the day in the billing time zone, for the Ordway coupon expiration date only.
// See `looseBillingStartOfDayUnix` for the non-throwing contract.
function looseBillingEndOfDayUnix(date: string): number | undefined {
  const parsed = parseDateLoosely(date);
  return parsed == null
    ? undefined
    : Math.floor(parsed.add({ days: 1 }).toDate(BILLING_TIME_ZONE).getTime() / 1000) - 1;
}

/**
 * Synchronous permission check. It is here because billing is the only consumer that must check
 * permissions outside the React tree. Other code must use the `usePermissions` hook. `checkPermission`
 * reads account permissions from `user.account_permissions` before workspace roles, so null
 * `roles`/`workspaceId` is enough for an account-level permission such as `account/billing/edit`.
 *
 * Call `applyMasqReadOnly` here too. `PermissionsProvider` applies it for the React tree, and
 * this call does not go through it.
 */
function hasAccountPermission(permission: AccountPermission): boolean {
  const user = userStore.get();
  const account = accountStore.get();

  if (user == null || account == null) {
    return false;
  }

  const effectiveUser = applyMasqReadOnly(user, account, masqReadOnlyStore.get());

  return checkPermission(effectiveUser, account, null, permission, null);
}

// The read-only toggle is only applicable in a masquerade session, so also check the account masq
// flag. The permission checks already stop edits in a read-only masquerade. This flag only shows the
// read-only *message*.
function isMasqReadOnly(account: AugmentedAccount | null): boolean {
  return account?.is_masq === true && masqReadOnlyStore.get();
}

// The atoms and their computeds are at module scope, because a computed must list its atoms as
// dependencies and an object literal cannot reference its own siblings. The `billing` object below
// exposes them with shorthand, so `billing.subscription` is the atom defined here.

// Billing data atoms.
const subscription = atom<BillingSubscription | undefined>(undefined);
const coupon = atom<BillingCoupon | undefined>(undefined);
const taxRate = atom<TaxRate | undefined>(undefined);
/** The plan that matches `paramPlanSlug`, found in `plans`. */
const paramPlan = atom<StripeBillingPlan | null>(null);
/** Legacy Stripe billing info. Undefined for an Ordway account. */
const billingInfo = atom<BillingInfo | undefined>(undefined);
/** Stripe plans in the shared `BillingPlan` shape. Undefined for an Ordway account. */
const plans = atom<StripeBillingPlan[] | undefined>(undefined);
const tiers = atom<Tier[] | undefined>(undefined);
/** Ordway billing data. Undefined for a legacy Stripe or Metronome account. */
const customer = atom<BillingOrdwayCustomerModel | undefined>(undefined);
/** The upcoming Ordway subscription, in the shared `BillingPlan` shape. */
const futureSubscription = atom<OrdwaySubscriptionBillingPlan | undefined>(undefined);
const products = atom<OrdwaySubscriptionProduct[] | undefined>(undefined);
/** Account usage (allotments and per-environment usage). Loaded on demand with `loadUsage`. */
const usage = atom<BillingUsage | undefined>(undefined);
/**
 * The Ordway plans catalog, loaded on demand with `fetchAvailablePlans`. Unused for a Stripe account,
 * whose plans arrive with `billing/info`.
 */
const availablePlans = atom<OrdwayPlanBillingPlan[] | undefined>(undefined);
/** From the `billing/overage_estimate` endpoint. Read through {@link billing.billingEstimates}. */
const ordwayBillingEstimate = atom<BillingEstimates | undefined>(undefined);
const retentionDiscountEligible = atom(false);
/**
 * The account's saved card as a {@link PaymentCard}, or null when there is no card on file. Set from the
 * subscription's `stripe_customer`, or from the Ordway payment method that
 * {@link billing.fetchOrdwayCardDetails} loads. Read through {@link billing.cardDetails}.
 */
const card = atom<PaymentCard | null>(null);
const initialized = atom(false);

/**
 * Whether the last `initialize` run ended in a caught error.
 *
 * Recorded because the load swallows its errors for the vendors and account states that fail
 * routinely, so it marks itself initialized with none of its data set. That is also what a
 * *successful* load looks like on an account with no effective subscription — the Ordway and
 * Metronome paths always clear `billingInfo`, and Ordway leaves `subscription` undefined — so
 * missing data cannot tell the two apart. Only the loader knows which happened.
 */
const loadFailed = atom(false);

/**
 * Whether a newer change supersedes this run, so it must not write to the billing atoms. It is a
 * function, and not an inline `signal?.aborted === true`, so the flag is read again after each await.
 * The caller aborts while the request is open, and TypeScript narrows an inline check away.
 */
function isSuperseded(signal: AbortSignal | undefined): boolean {
  return signal?.aborted === true;
}

// Account-derived flags.
/**
 * Whether the account has an `ordway_id`, i.e. whether there is a record to look up in Ordway.
 *
 * This is **not** "Ordway bills this account". That is {@link isOrdwayVendor}. See the note on the
 * vendor flags below.
 */
const isOrdwayCustomer = computed(
  accountStore,
  (account) => account?.ordway_id != null && account.ordway_id.length > 0,
);
const isOrdwayCustomerWaitingInitialization = computed(
  accountStore,
  (account) => account?.ordway_id?.startsWith('TEMP-') === true,
);
/**
 * Whether churned profiles are billable: the plan flag, or any Ordway account. Ordway always bills
 * high-watermark totals, whatever `plan_include_churn` says.
 */
const isChurnIncluded = computed(
  [accountStore, isOrdwayCustomer],
  (account, ordwayCustomer) => account?.plan_include_churn === true || ordwayCustomer,
);
/**
 * The three vendor flags: **which billing system bills this account**. {@link resolveBillingVendor}
 * gives the answer, so they agree with the backend even when the API omits `billing_vendor`.
 *
 * The two `*Customer` flags near them ask a different question — **is there a record in that
 * system** — and the answer is not always the same:
 *
 * - {@link isOrdwayVendor} — Ordway bills this account.
 * - {@link isOrdwayCustomer} — the account has an `ordway_id`, so there is something to look up in
 *   Ordway. An account that Ordway bills does **not** always have one, because checkout creates the
 *   id. An account that Metronome bills can keep a stale one. Both flags are necessary.
 * - {@link isStripeCustomer} — the subscription has a `stripe_customer`. Metronome has the Stripe
 *   shape on the wire, so a Metronome account also satisfies this. Use it for questions about the
 *   Stripe customer record itself, such as credit-purchase gating.
 *
 * Rule: **who bills them → `*Vendor`. Is there a record to work with → `*Customer`.**
 */
const isLegacyStripeVendor = computed(
  accountStore,
  (account) => account != null && resolveBillingVendor(account) === BillingSystem.Stripe,
);
const isOrdwayVendor = computed(
  accountStore,
  (account) => account != null && resolveBillingVendor(account) === BillingSystem.Ordway,
);
/**
 * The fallback chain never *gives* Metronome. It gives only Ordway or legacy Stripe
 * (`services/domains/accounts/billing_vendor.go:13`), so a Metronome account is always explicit.
 */
const isMetronomeVendor = computed(
  accountStore,
  (account) => account != null && resolveBillingVendor(account) === BillingSystem.Metronome,
);
const isVerificationRejected = computed(
  accountStore,
  (account) => account?.verification_state === VerificationState.Rejected,
);

// Reactive form of the read-only-masquerade check for components. Both stores are dependencies, so a
// component that reads it with `useStore` re-renders when the account or the masq toggle changes.
const isMasqReadOnlyState = computed([accountStore, masqReadOnlyStore], (account) => isMasqReadOnly(account));

// Derived, reactive billing state.

const isPremiumTrial = computed([accountStore, subscription, billingInfo], (account, sub, info) => {
  if (isMetronomeSubscription(sub)) {
    return false;
  }
  if (sub?.billingSystem === BillingSystem.Ordway) {
    return account?.planFeatures.premium_trial.enabled === true;
  }

  return info?.premium_trial ?? false;
});

/** The account has a payment method, but its trial is not complete. */
const isPendingCheckout = computed(
  [accountStore, subscription, customer, futureSubscription, isPremiumTrial],
  (account, sub, cust, future, premiumTrial) => {
    if (!isOrdwaySubscription(sub)) {
      return false;
    }

    const accountState = account?.account_state;
    const isCurrentOrFree = accountState === AccountState.Current || accountState === AccountState.Free;

    return (
      (isCurrentOrFree || cust?.has_credit_card === true) && // active account, or a payment method
      (sub.isTrial || premiumTrial) && // on a free trial
      future != null // has an upcoming subscription
    );
  },
);

/** Trial state for a Metronome, Ordway, or legacy Stripe account. */
const isTrial = computed(
  [accountStore, subscription, billingInfo, isPendingCheckout],
  // fallow-ignore-next-line complexity -- inherent to the billing domain mapping
  (account, sub, info, pendingCheckout) => {
    if (isMetronomeSubscription(sub)) {
      return sub.is_trial;
    }

    const isOrdwayAccount = account?.ordway_id != null && account.ordway_id.length > 0;

    if (isOrdwayAccount || sub?.billingSystem === BillingSystem.Ordway) {
      const isWaitingInitialization = account?.ordway_id?.startsWith('TEMP-') === true;
      return isWaitingInitialization || (isOrdwaySubscription(sub) && sub.isTrial === true) || pendingCheckout;
    }

    const billingInfoTrial = info == null ? undefined : info.premium_trial || info.basic_trial;
    return billingInfoTrial ?? account?.suspend_trial_at != null;
  },
);

/**
 * The plan charges nothing this period, because it is a trial.
 *
 * A premium trial on a legacy Stripe account is the exception: it carries a price the account is
 * charged, which is why the plan card states that price and blanks every other trial's.
 */
const isFreeTrialPlan = computed(
  [subscription, isPremiumTrial, isOrdwayCustomer],
  (sub, premiumTrial, ordwayCustomer) => {
    const plan = isStripeSubscription(sub) ? sub.plan : sub;
    if (plan?.isTrial !== true) {
      return false;
    }
    return ordwayCustomer || !premiumTrial;
  },
);

/** The pinned plan, or null. Ordway and Metronome accounts have no pinned plan. */
const pinnedPlan = computed([subscription, billingInfo, plans], (sub, info, planList) => {
  if (sub?.billingSystem === BillingSystem.Ordway || isMetronomeSubscription(sub)) {
    return null;
  }

  const pinnedPlanId = info?.pinned_plan_id;
  if (pinnedPlanId == null) {
    return null;
  }

  return planList?.find((plan) => plan.id === pinnedPlanId) ?? null;
});

/**
 * The Essentials plan. An Ordway account reads the Essentials entry in the `availablePlans` catalog. A
 * Stripe account reads the plan in `plans` with `ESSENTIALS_PLAN_SLUG`. Undefined until those plans
 * load.
 */
const essentialsPlan = computed(
  [isOrdwayCustomer, availablePlans, plans],
  (ordwayCustomer, ordwayPlans, stripePlans): OrdwayPlanBillingPlan | StripeBillingPlan | undefined => {
    if (ordwayCustomer) {
      return (ordwayPlans ?? []).find((plan) => plan.planType === PlanType.Essentials);
    }

    return (stripePlans ?? []).find((plan) => plan.slug === ESSENTIALS_PLAN_SLUG);
  },
);

/**
 * The start of the current billing period, as unix seconds. An Ordway account reads the subscription's
 * period or service start, at start of day in the billing time zone. Other accounts read `plan_period_start`.
 */
const billingPeriodStart = computed([subscription, accountStore], (sub, account) => {
  if (isMetronomeSubscription(sub)) {
    return positiveOrUndefined(sub.billing_period_start);
  }
  if (isOrdwaySubscription(sub)) {
    // The Ordway wire model sends an unset date as an empty string, not null.
    const startDate = isEmptyString(sub.billing_period_start_date)
      ? sub.service_start_date
      : sub.billing_period_start_date;
    return isEmptyString(startDate) ? undefined : looseBillingStartOfDayUnix(startDate);
  }

  return account?.plan_period_start;
});

/**
 * The end of the current billing period, as unix seconds. An Ordway account reads one second before the
 * UTC start of day of `usage_reset_date`. Other accounts read `plan_period_end`.
 */
const billingPeriodEnd = computed([subscription, accountStore], (sub, account) => {
  if (isMetronomeSubscription(sub)) {
    return positiveOrUndefined(sub.billing_period_end);
  }
  if (isOrdwaySubscription(sub)) {
    return isEmptyString(sub.usage_reset_date) ? undefined : utcStartOfDayUnix(sub.usage_reset_date) - 1;
  }

  return account?.plan_period_end;
});

/**
 * The profile allotment. It comes from the Ordway subscription, the pinned plan, the active Stripe plan,
 * or, for an account with no plan, the account usage. `loadUsage` loads usage on demand, so the
 * no-plan fallback is 0 until then. The other allotments below work the same way.
 */
const includedProfiles = computed([subscription, pinnedPlan, usage], (sub, pinned, accountUsage) => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.includedProfiles;
  }

  if (pinned != null) {
    return pinned.includedProfiles;
  }

  return (isStripeSubscription(sub) ? sub.plan?.includedProfiles : undefined) ?? accountUsage?.customersAllotted ?? 0;
});

const includedMessages = computed([subscription, pinnedPlan, usage], (sub, pinned, accountUsage) => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.includedMessages;
  }

  if (pinned != null) {
    return pinned.includedMessages;
  }

  return (isStripeSubscription(sub) ? sub.plan?.includedMessages : undefined) ?? accountUsage?.messagesAllotted ?? 0;
});

const includedObjects = computed([subscription, pinnedPlan, usage], (sub, pinned, accountUsage) => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.includedObjects;
  }

  if (pinned != null) {
    return pinned.includedObjects;
  }

  return (isStripeSubscription(sub) ? sub.plan?.includedObjects : undefined) ?? accountUsage?.objectsAllotted ?? 0;
});

/** The monthly-active-user allotment. Ordway only. */
const includedMau = computed([subscription], (sub) => {
  return isOrdwaySubscription(sub) ? (sub.includedMau ?? 0) : 0;
});

/**
 * The total billable customer count. Account usage includes only billable workspaces. If all
 * environments are billed, the account-level total is correct. If one environment is not billed, that
 * total excludes it, so add the per-environment counts (see `deriveUsageMetrics`).
 */
const totalCustomerCount = computed([usage], (accountUsage) => {
  const environments = accountUsage?.environments ?? [];
  if (hasUnbilledEnvironment(environments)) {
    return environments.reduce((sum, environment) => sum + environment.customersTotal, 0);
  }
  return accountUsage?.customersTotal ?? 0;
});

/** Why an action is disabled for the given permission. An empty string when the action is permitted. */
function disabledMessageForPermission(
  permission: AccountPermission,
  permissionMessage: string = NO_BILLING_PERMISSION_MESSAGE,
): string {
  if (isVerificationRejected.get()) {
    return VERIFICATION_REJECTED_TOOLTIP;
  }
  if (isMasqReadOnly(accountStore.get())) {
    return MASQ_READ_ONLY_MESSAGE;
  }
  if (!hasAccountPermission(permission)) {
    return permissionMessage;
  }
  return '';
}

/** Upgrades are disabled by a rejected verification, a read-only masquerade, or a missing permission. */
const isUpgradeDisabled = computed(
  [accountStore, userStore, masqReadOnlyStore, isVerificationRejected],
  (account, _user, _masqReadOnly, verificationRejected) => {
    return verificationRejected || isMasqReadOnly(account) || !hasAccountPermission('account/billing/edit');
  },
);

const upgradeDisabledMessage = computed([accountStore, userStore, masqReadOnlyStore, isVerificationRejected], () =>
  disabledMessageForPermission('account/billing/edit'),
);

// --- Plan identity & naming. ---

const planType = computed([subscription], (sub): PlanType | undefined => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.planType;
  }

  return isStripeSubscription(sub) ? sub.plan?.planType : undefined;
});

/** The plan type of the upcoming subscription. Ordway only. */
const futurePlanType = computed([subscription, futureSubscription], (sub, future): PlanType | undefined => {
  if (isOrdwaySubscription(sub)) {
    return future?.planType;
  }

  return undefined;
});

/**
 * The plan an Ordway account moves onto, and when. A trial moves onto its paid plan the same way,
 * which the trial states on its own, so it is not a plan change.
 */
const planChange = computed(
  [subscription, futureSubscription, isTrial],
  (sub, future, trial): ScheduledPlanChange | undefined => {
    if (!isOrdwaySubscription(sub) || future == null || trial) {
      return undefined;
    }

    return scheduledPlanChange(sub, future);
  },
);

/** The plan slug. An empty string for Ordway and Metronome accounts, which have no slug. */
const planSlug = computed([subscription], (sub): string | undefined => {
  if (isOrdwaySubscription(sub) || isMetronomeSubscription(sub)) {
    return '';
  }

  return isStripeSubscription(sub) ? sub.plan?.slug : undefined;
});

/** True for a Basic plan and for an Essentials plan. */
const isBasic = computed([subscription, billingInfo, planType], (sub, info, type) => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    if (type == null) {
      return false;
    }

    return type === PlanType.Basic || type === PlanType.Essentials;
  }

  return info?.basic ?? false;
});

/** True for a Premium plan and for a premium trial. */
const isPremium = computed([subscription, billingInfo, isPremiumTrial, planType], (sub, info, premiumTrial, type) => {
  if (isMetronomeSubscription(sub)) {
    return type === PlanType.Premium;
  }
  if (isOrdwaySubscription(sub)) {
    return premiumTrial || type === PlanType.Premium;
  }

  return info?.premium ?? false;
});

/** Premium or Enterprise. The tiers that hold an annual contract. Includes a premium trial via `isPremium`. */
const isPremiumOrAbove = computed([isPremium, planType], (premium, type): boolean => {
  return premium || type === PlanType.Enterprise;
});

/**
 * A basic (Essentials) trial. An Ordway account derives it from the trial and basic computeds. A legacy
 * Stripe account reads the `basic_trial` flag.
 */
const isBasicTrial = computed([subscription, isTrial, isBasic, billingInfo], (sub, trial, basic, info) => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return trial && basic;
  }

  return info?.basic_trial ?? false;
});

const isFlex = computed([accountStore], (account) => account?.plan_slug === FLEX_PLAN_SLUG);

/** The short plan name. A premium trial adds a "Trial" suffix. */
const planNameShortened = computed(
  [subscription, billingInfo, plans, paramPlan, isBasic, isPremium, isTrial, isPremiumTrial, isFlex],
  // fallow-ignore-next-line complexity -- inherent to the billing domain mapping
  (sub, info, planList, param, basic, premium, trial, premiumTrial, flex): string => {
    if (info == null && sub == null) {
      return '';
    }

    let plan: string;

    if (flex) {
      // Temporary: billing data has no Flex plan name yet. Remove this branch when it does.
      plan = 'Flex';
    } else if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
      plan = sub.name;
    } else if (basic) {
      // The main Essentials plan, from January 2023.
      const subscriptionPlanId = isStripeSubscription(sub) ? sub.plan?.id : undefined;
      plan = planList?.find((candidate) => candidate.id === subscriptionPlanId)?.name ?? 'Essentials';
    } else if (trial && premium) {
      // A premium trial shows the plan from the URL param, or a generic name.
      plan = param != null ? param.name : 'Premium';
    } else {
      // An active custom plan, premium or not.
      plan = (isStripeSubscription(sub) ? sub.plan?.name : undefined) ?? '';
    }

    if (premiumTrial) {
      return `${plan} Trial`;
    }

    return plan;
  },
);

/** The plan display name, or the short plan name when there is none. */
const planDisplayName = computed([subscription, planNameShortened], (sub, nameShort): string => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return nameShort;
  }

  return (isStripeSubscription(sub) ? sub.plan?.display_name : undefined) ?? nameShort;
});

const isBuilder = computed([accountStore], (account) => account?.plan_slug === PlanType.Builder);

/** Alias of `isBuilder`. */
const isBuilderPlan = computed([isBuilder], (builder) => builder);

const isFreeBuilder = computed([isBuilder, accountStore], (builder, account) => {
  return builder && account?.account_state === AccountState.Free;
});

/** The plan pays for its sends with credits from the wallet, not on a monthly bill. */
const usesCredits = computed([isBuilder, isFlex], (builder, flex) => builder || flex);

/** The plan types the account can upgrade to, by product app. */
const upgradePlanTypes = computed(
  [isPremiumTrial, accountStore, planType],
  (premiumTrial, account, type): Record<ProductApp, PlanType> => {
    return getUpgradePlanTypes(premiumTrial, account?.planFeatures.messaging.expired ?? false, type);
  },
);

// --- Subscription lifecycle & date derivations. ---

/** A typed view of the loose `custom_fields` bag on an Ordway customer. */
function ordwayCustomerCustomFields(customer: BillingOrdwayCustomerModel): OrdwayCustomerCustomFields {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return (customer.custom_fields ?? {}) as OrdwayCustomerCustomFields;
}

/** An active subscription that is not a trial. */
const isSubscriptionActive = computed([subscription, isTrial], (sub, trial) => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return trial === false;
  }

  return isStripeSubscription(sub) ? sub.active : false;
});

/**
 * The pinned start date, as unix seconds. An Ordway account has no pinned plan in the Stripe sense, so
 * the result is undefined.
 */
const pinnedStartDate = computed([subscription, billingInfo], (sub, info): number | undefined => {
  if (isOrdwaySubscription(sub) || isMetronomeSubscription(sub)) {
    return undefined;
  }

  return info?.pinned_start_date;
});

/**
 * The formatted subscription start date. An Ordway account formats the UTC start of day of the upcoming
 * subscription, or of the effective one. A Stripe account formats the pinned start date.
 *
 * All show the date in the billing time zone, like the other billing dates.
 */
const formattedStartDate = computed([subscription, futureSubscription, billingInfo], (sub, future, info) => {
  if (isMetronomeSubscription(sub)) {
    return formatDateTime(sub.contract_start * 1000, 'medium', {
      day: 'numeric',
      month: 'long',
      timeZone: BILLING_TIME_ZONE,
    });
  }
  if (isOrdwaySubscription(sub)) {
    const date = future?.billing_start_date ?? sub.billing_start_date;
    // A real Ordway subscription always has a `billing_start_date`, so only sparse test data gets here.
    // This early return is clearer than the empty-string fallback in `formatDateTime`.
    if (isEmptyString(date)) {
      return '';
    }
    return formatDateTime(utcStartOfDayUnix(date) * 1000, 'medium', {
      day: 'numeric',
      month: 'long',
      timeZone: BILLING_TIME_ZONE,
    });
  }

  // This is not an Ordway account, so read `billingInfo.pinned_start_date` directly. The
  // `pinnedStartDate` computed gives the same value, but it makes a diamond over `subscription`.
  return formatDateTime((info?.pinned_start_date ?? 0) * 1000, 'medium', {
    day: 'numeric',
    month: 'long',
    timeZone: BILLING_TIME_ZONE,
  });
});

// A contract term boundary is a fixed calendar date, so it must read the same in every zone. This
// formats in UTC rather than the viewer's zone, which would shift the day.
const CONTRACT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** A `YYYY-MM-DD` Ordway contract date, formatted, or undefined when it is unset. */
function formatOrdwayContractDate(date: string | null | undefined): string | undefined {
  if (date == null || isEmptyString(date)) {
    return undefined;
  }
  return CONTRACT_DATE_FORMATTER.format(utcStartOfDayUnix(date) * 1000);
}

/** A unix-seconds Stripe contract date, formatted, or undefined when it is unset. */
function formatStripeContractDate(unixSeconds: number | null | undefined): string | undefined {
  if (unixSeconds == null || unixSeconds <= 0) {
    return undefined;
  }
  return CONTRACT_DATE_FORMATTER.format(unixSeconds * 1000);
}

/** The start of the current contract term, formatted. */
const contractTermStart = computed([subscription], (sub): string | undefined => {
  if (isOrdwaySubscription(sub)) {
    return formatOrdwayContractDate(sub.current_term_start_date);
  }
  return isStripeSubscription(sub) ? formatStripeContractDate(sub.subscription_term_start) : undefined;
});

/** The end of the current contract term, formatted. */
const contractTermEnd = computed([subscription], (sub): string | undefined => {
  if (isOrdwaySubscription(sub)) {
    return formatOrdwayContractDate(sub.current_term_end_date);
  }
  return isStripeSubscription(sub) ? formatStripeContractDate(sub.subscription_term_end) : undefined;
});

/** When the contract renews. Ordway renews the day after the term ends; Stripe renews on the period end. */
const contractRenewalDate = computed([subscription], (sub): string | undefined => {
  if (isOrdwaySubscription(sub)) {
    const end = sub.current_term_end_date;
    if (end == null || isEmptyString(end)) {
      return undefined;
    }
    const dayAfterEnd = utcStartOfDayUnix(end) + 24 * 60 * 60;
    return CONTRACT_DATE_FORMATTER.format(dayAfterEnd * 1000);
  }
  return isStripeSubscription(sub) ? formatStripeContractDate(sub.subscription_term_end) : undefined;
});

/** Whether the contract renews on its own at the end of the term. */
const contractRenewsAutomatically = computed([subscription], (sub): boolean => {
  if (isOrdwaySubscription(sub) || isStripeSubscription(sub)) {
    return sub.auto_renew === true;
  }
  return false;
});

/**
 * The cadence the base charge recurs on. An Ordway account reads the base product billing period. A
 * Stripe account is annual when the subscription `annual` flag is set — a standard plan keeps its
 * `interval_name` as "month" even when billed annually, so the flag decides before the interval;
 * otherwise the plan interval (quarter, biannual, month) decides. Metronome bills monthly for now.
 */
const baseBillingCadence = computed([subscription], (sub): BillingCadence => {
  if (isMetronomeSubscription(sub)) {
    return BillingCadence.Monthly;
  }
  if (isOrdwaySubscription(sub)) {
    const base = sub.plans?.find((product) => baseOrdwayProductIds().includes(product.product_id));
    return ordwayCadence(base?.billing_period);
  }
  if (isStripeSubscription(sub)) {
    return sub.annual ? BillingCadence.Annual : stripeCadence(sub.plan?.interval_name);
  }

  return BillingCadence.Unknown;
});

/**
 * The plan's own charge recurs monthly. A longer term (quarterly, semi-annual, annual) is invoiced
 * apart from the monthly cycle, which then collects only overages. An unread cadence bills monthly, so
 * the base price stays on the estimate.
 */
const isBilledMonthly = computed([baseBillingCadence], (cadence) => !isNonMonthlyContract(cadence));

/** The cycle carries overages alone, because the plan's own charge is invoiced apart from it. */
const isOverageOnlyCycle = computed([usesCredits, isBilledMonthly], (credits, monthly) => !credits && !monthly);

/**
 * The next charge carries overages alone. A plan that starts when the cycle resets decides it,
 * because the current plan is not charged again. Its base is charged only when it bills monthly and
 * is not a lump sum.
 */
const isOverageOnlyNextCharge = computed([isOverageOnlyCycle, planChange], (overageOnly, change) => {
  if (change?.timing === PlanChangeTiming.EndOfCycle) {
    return !change.isBaseOnNextCharge;
  }
  return overageOnly;
});

const isLumpSumPlan = computed([subscription, billingInfo], (sub, info) => {
  if (isMetronomeSubscription(sub)) {
    return false;
  }
  if (isOrdwaySubscription(sub)) {
    return sub.isLumpSum === true;
  }

  return info?.lump_sum === true;
});

/**
 * When the subscription cancels, as UTC start-of-day unix seconds. It is defined only for an Ordway
 * subscription that is pending cancellation. The date comes from the customer custom fields, or from the
 * subscription when the customer has none.
 */
const cancelsAt = computed([accountStore, subscription, customer], (account, sub, cust): number | undefined => {
  if (account?.account_state !== AccountState.PendingCancellation) {
    return undefined;
  }

  if (!isOrdwaySubscription(sub)) {
    return undefined;
  }

  let date: string | null | undefined;

  if (cust != null) {
    date = ordwayCustomerCustomFields(cust).cancellation_date;
  }

  if (isEmptyString(date)) {
    date = sub.cancellation_date;
  }

  return isEmptyString(date) ? undefined : utcStartOfDayUnix(date);
});

const canCancel = computed([accountStore, subscription, billingInfo], (account, sub, info) => {
  if (account?.account_state === AccountState.PendingCancellation) {
    return false;
  }
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.canCancel;
  }

  return info?.cancellable ?? false;
});

/**
 * Only an Ordway account on a free trial can downgrade to Builder. Ordway models each trial with one
 * Free Trial product.
 *
 * Gated on `builder_offer_enabled`, so pausing the Builder offering hides every self-serve entry
 * point that reads this: the cancel flow's save-offer step and its `?signupType=builder` deep link.
 */
const isEligibleForBuilderDowngrade = computed([accountStore, subscription, isTrial], (account, sub, trial) => {
  if (!accountHasFeatureFlag(account, AccountFeatureFlag.BuilderOfferEnabled)) {
    return false;
  }

  return isOrdwaySubscription(sub) ? trial : false;
});

// --- Pricing, rate & summary derivations. ---

/** The base plan price, in cents. */
const planPrice = computed([subscription], (sub): number | undefined => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.basePrice;
  }

  return isStripeSubscription(sub) ? sub.plan?.basePrice : undefined;
});

/** The overage rate for each customer, as a dollar string. */
const meteredCustomerRate = computed([subscription, isTrial], (sub, trial): string | undefined => {
  if (isMetronomeSubscription(sub)) {
    return sub.profileOverageRate;
  }
  if (isOrdwaySubscription(sub)) {
    if (trial) {
      return Plans.Essentials.profileOverageRate;
    }

    if (sub.customerOverageRate === '') {
      return sub.profileOverageRate;
    }

    return sub.customerOverageRate;
  }

  return isStripeSubscription(sub) ? sub.plan?.customerOverageRate : undefined;
});

/** The overage rate for each object, as a dollar string. */
const meteredObjectRate = computed([subscription], (sub): string | undefined => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.objectOverageRate;
  }

  return isStripeSubscription(sub) ? sub.plan?.objectOverageRate : undefined;
});

/**
 * The overage rate for each email, as a dollar string. A Stripe plan carries no rate and returns an
 * empty string, so callers must read an unparseable rate as unknown rather than as free.
 */
const meteredMessageRate = computed([subscription], (sub): string | undefined => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.messageOverageRate;
  }

  return isStripeSubscription(sub) ? sub.plan?.messageOverageRate : undefined;
});

/** The overage rate for each monthly active user, as a dollar string. Ordway MAU plans only. */
const meteredMauRate = computed([subscription], (sub): string | undefined => {
  return isOrdwaySubscription(sub) ? sub.mauOverageRate : undefined;
});

/** Whether the plan meters active users instead of stored profiles. Ordway MAU plans only. */
const isMauPricing = computed([subscription], (sub): boolean => {
  return isOrdwaySubscription(sub) ? sub.isMauPricing : false;
});

/** The billing interval name, e.g. "Monthly" or "Yearly". */
const intervalDisplayName = computed([subscription], (sub): string => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return sub.intervalDisplayName;
  }

  return (isStripeSubscription(sub) ? sub.plan?.intervalDisplayName : undefined) ?? 'unknown';
});

/**
 * The CDP Premium price, in cents. An Ordway account finds the CDP Premium product on the subscription
 * and changes its dollar `list_price` to cents. The result is undefined when the subscription has no CDP
 * Premium product.
 */
const cdpPremiumPrice = computed([subscription], (sub): number | undefined => {
  if (isMetronomeSubscription(sub)) {
    return undefined;
  }
  if (isOrdwaySubscription(sub)) {
    const cdpPremiumProduct = (sub.plans ?? []).find(
      (product) => product.product_id === ordwayProductIds()[ProductName.CDPPremium],
    );

    if (cdpPremiumProduct == null) {
      return undefined;
    }

    return Number.parseFloat(cdpPremiumProduct.list_price) * 100;
  }

  return isStripeSubscription(sub) ? sub.plan?.cdpPremiumPrice : undefined;
});

/**
 * When the coupon ends, as unix seconds. An Ordway account reads `expiration_date` as end of day in the
 * billing time zone. A
 * Stripe account reads `coupon_ends_at`. Only the Ordway coupon model has `expiration_date`, so the
 * coupon union must narrow first.
 */
const couponEndsAt = computed([subscription, coupon], (sub, couponValue): number | undefined => {
  if (isMetronomeSubscription(sub)) {
    return undefined;
  }
  if (isOrdwaySubscription(sub)) {
    const expiration =
      couponValue != null && 'expiration_date' in couponValue ? couponValue.expiration_date : undefined;
    // The Ordway wire model sends an unset date as an empty string, which would give NaN.
    return isEmptyString(expiration) ? undefined : looseBillingEndOfDayUnix(expiration);
  }

  // Stripe serializes an unset end date as 0, so a coupon with no end reads as no date rather than
  // as the epoch.
  return isStripeSubscription(sub) ? positiveOrUndefined(sub.coupon_ends_at) : undefined;
});

/**
 * The most recent `updated_at` of all the environments. It is undefined until `loadUsage` loads usage, and
 * when there are no environments.
 */
const usageSyncedAt = computed([usage], (accountUsage): number | undefined => {
  const environments = accountUsage?.environments ?? [];
  if (environments.length === 0) {
    return undefined;
  }

  // Each workspace syncs its usage separately, usually within seconds of the others. A missing
  // `updatedAt` compares as NaN, so it never wins the maximum, and sparse test data cannot throw.
  return environments.reduce<number | undefined>((newest, environment) => {
    if (newest == null || environment.updatedAt > newest) {
      return environment.updatedAt;
    }
    return newest;
  }, undefined);
});

/**
 * When usage resets in the current billing period, as unix seconds. An Ordway account reads
 * `usage_reset_date` at UTC start of day, because usage dates ignore times and time zones. Other accounts
 * read `plan_period_end`.
 */
const usageResetsAt = computed([subscription, accountStore], (sub, account): number | undefined => {
  if (isMetronomeSubscription(sub)) {
    return positiveOrUndefined(sub.billing_period_end);
  }
  if (isOrdwaySubscription(sub)) {
    return isEmptyString(sub.usage_reset_date) ? undefined : utcStartOfDayUnix(sub.usage_reset_date);
  }

  return account?.plan_period_end;
});

/**
 * When the current billing period is charged, as unix seconds.
 *
 * No vendor sends a charge date, so it is derived from the moment usage resets — the same instant
 * Your plan states as "Usage will reset on", the reactivate modal as the next billing date, and the
 * inbox-preview balance as its reset. Reading {@link usageResetsAt}, rather than adding a day to the
 * period end, is what keeps those surfaces naming one date: the two boundaries mean different things
 * per vendor, but the rollover means the same thing everywhere.
 *
 * The precision it carries also differs per vendor, and is deliberately kept rather than rounded off.
 * Ordway sends a date with no time or zone, which the transform anchors to UTC midnight; Stripe and
 * Metronome send the exact instant the subscription renews. Truncating the latter to a day would
 * throw away a time we actually know, and shift the date for a reader whose zone disagrees with UTC.
 */
const nextChargeDate = computed([usageResetsAt], (resetsAt): number | undefined => {
  return positiveOrUndefined(resetsAt);
});

/**
 * Whether the coupon still applies on the next charge, decided per rail.
 *
 * Ordway sends calendar dates, so the coupon's expiration and the charge date are compared as UTC
 * days. Every other rail sends real instants, compared directly.
 */
const isCouponActiveForNextCharge = computed(
  [subscription, coupon, couponEndsAt, nextChargeDate],
  (sub, couponValue, endsAt, chargeDate): boolean => {
    if (isOrdwaySubscription(sub)) {
      const expiration =
        couponValue != null && 'expiration_date' in couponValue ? couponValue.expiration_date : undefined;
      return couponActiveForChargeDate(expiration, sub.usage_reset_date);
    }
    return couponActiveForCharge(endsAt, chargeDate);
  },
);

/**
 * The overage estimates. An Ordway account reads the estimate that
 * {@link billing.setOrdwayBillingEstimate} stores. A legacy Stripe account reads `billing_estimates`.
 */
const billingEstimates = computed(
  [subscription, billingInfo, ordwayBillingEstimate],
  (sub, info, ordwayEstimate): BillingEstimates | undefined => {
    if (isMetronomeSubscription(sub)) {
      return undefined;
    }
    if (isOrdwaySubscription(sub)) {
      return ordwayEstimate;
    }

    // The Stripe wire model has no `mau_overage`, which is Ordway only, so the type makes it optional.
    return info?.billing_estimates;
  },
);

/**
 * Whether {@link billing.billingEstimates} reports the whole bill, and not the overages alone.
 *
 * The legacy Stripe estimate sums the plan price, every overage, the coupon and the tax into one
 * figure (`services/billing/estimate.go`). The Ordway endpoint reports its usage charges alone
 * (`services/ui_api/billing/payment.go`), and Metronome reports nothing.
 *
 * Read from the subscription, as the estimate is, so the two cannot disagree about the same account.
 */
const reportsWholeBill = computed([subscription], (sub) => {
  if (isMetronomeSubscription(sub) || isOrdwaySubscription(sub)) {
    return false;
  }
  return isStripeSubscription(sub);
});

// Payment-method and charge-status derivations. An Ordway account reads the Ordway `customer`. A Stripe
// account reads the subscription wire flags or `stripe_customer`.

const hasCard = computed([subscription, customer], (sub, cust) => {
  if (isMetronomeSubscription(sub)) {
    return sub.payment != null;
  }
  if (isOrdwaySubscription(sub)) {
    return cust?.has_credit_card === true;
  }
  return isStripeSubscription(sub) && sub.hasCard;
});

/** The account has ACH (direct debit) set up. */
const hasAchSetup = computed([subscription, customer], (sub, cust) => {
  if (isOrdwaySubscription(sub)) {
    return cust?.has_ach === true;
  }
  return isStripeSubscription(sub) && sub.has_ach_setup === true;
});

/** Always false for an Ordway account. */
const hasFailedOverageCharge = computed([subscription], (sub) => {
  if (!isStripeSubscription(sub)) {
    return false;
  }
  return sub.has_failed_overage_charge === true;
});

/** The raw Stripe customer. Undefined for an Ordway account. */
const stripeCustomer = computed([subscription], (sub) => {
  if (!isStripeSubscription(sub)) {
    return undefined;
  }
  return sub.stripe_customer;
});

/**
 * The subscription has a `stripe_customer` record.
 *
 * This is **not** a vendor check. Metronome has the Stripe shape on the wire, so a Metronome account also
 * satisfies it. For "the vendor is legacy Stripe", use {@link isLegacyStripeVendor}. Use this flag for
 * questions about the Stripe customer itself, such as credit-purchase gating.
 */
const isStripeCustomer = computed([subscription], (sub) => {
  if (!isStripeSubscription(sub)) {
    return false;
  }
  return sub.stripe_customer != null;
});

function isSuspendedOrOverdue(account: ReturnType<typeof accountStore.get>): boolean {
  const state = account?.account_state;
  return state === AccountState.Suspended || state === AccountState.Overdue;
}

const canRetryPayment = computed(
  [subscription, customer, billingInfo, accountStore, hasFailedOverageCharge],
  (sub, cust, info, account, failedOverage) => {
    if (isMetronomeSubscription(sub)) {
      return isSuspendedOrOverdue(account);
    }
    if (isOrdwaySubscription(sub)) {
      return (isSuspendedOrOverdue(account) && cust?.has_ach === false) || failedOverage;
    }
    return info?.can_retry_payment === true;
  },
);

function stripePaymentError(sub: StripeSubscriptionBillingData): string | null {
  const error = sub.stripe_customer?.last_payment_error;
  return error != null ? stripeFormatter(error) : null;
}

/** The most recent payment error, formatted for the user, or null. */
const lastPaymentError = computed([subscription, customer], (sub, cust) => {
  if (isMetronomeSubscription(sub)) {
    return sub.payment?.last_payment_error ?? null;
  }
  if (isOrdwaySubscription(sub)) {
    return cust?.payment_failure_reason ?? null;
  }
  return isStripeSubscription(sub) ? stripePaymentError(sub) : null;
});

/**
 * The billing service singleton.
 *
 * It is a plain object, and not a class or a module that re-exports all of its parts, to keep the public
 * API small. See the "limiting public APIs" pattern
 * (https://zenn.dev/dk_/articles/bc779b8fba68b0?locale=en#limiting-public-apis).
 *
 * The state is in nanostore atoms, so the UI can subscribe and re-render. The account-derived flags are
 * computeds over the shared `accountStore`, so they follow account changes with no extra wiring. The
 * service is on `window.__hydra__.billing` for the embedded apps.
 */
/**
 * One poll attempt's verdict: `true` on a confirmed success, `false` to keep polling. Every terminal
 * outcome throws its user-facing message, so the caller only has to distinguish "done" from "again".
 */
function readOrdwayAttempt(
  rail: OrdwayPurchaseRail,
  read: OrdwayPurchaseStatusRead,
  runId: string,
  notFoundSoFar: number,
): boolean {
  switch (read.kind) {
    case 'status': {
      if (read.status === 'success') {
        return true;
      }
      const error = ordwayPurchaseStatusError(read.status);
      if (error != null) {
        throw error;
      }
      return false;
    }
    case 'not-found':
      if (notFoundSoFar < ORDWAY_PURCHASE_NOT_FOUND_TOLERANCE) {
        return false;
      }
      throw reportIndeterminateOrdwayPurchase(
        rail,
        `Ordway ${rail.label} purchase run ${runId} was not found after ${notFoundSoFar + 1} status reads`,
      );
    // A token this build does not know says nothing about the run. Ending here would settle a healthy
    // purchase as indeterminate on its first read, and lock buying with it; polling on lets a new
    // non-terminal token resolve to a real outcome, and leaves a genuinely stuck run to the timeout,
    // which reports the same indeterminate result anyway.
    case 'unrecognized':
    case 'unavailable':
      return false;
    default: {
      const exhaustive: never = read;
      throw new Error(String(exhaustive));
    }
  }
}

/**
 * Builds the status poll for one Ordway purchase rail. Every rail polls the same way — the loop, the
 * 404 tolerance, the timeout, and the "lost the account" case are all charge-safety rules that must
 * not diverge between them — so only the status request itself is supplied per rail.
 *
 * Each rail gets its own {@link pollTask}, so cancelling one form's poll never abandons the other's.
 *
 * Resolves `true` only on a confirmed `success`, or `false` when it was cancelled via `cancelAll`;
 * throws the user-facing message for a decline, a failure, an indeterminate outcome, a timeout, or a
 * lost account. Callers must treat a non-`true` result as "not confirmed" and skip reporting success.
 */
function ordwayPurchasePollTask(
  rail: OrdwayPurchaseRail,
  readStatus: (accountId: string, runId: string) => Promise<OrdwayPurchaseStatusRead>,
) {
  return pollTask(async (signal, runId: string): Promise<boolean> => {
    const account = accountStore.get();
    if (account == null) {
      // The purchase POST already started the workflow, so it is running with no account to observe it
      // through. Report the unknown outcome; resolving `false` would read as a cancelled poll and leave
      // a possibly-charged purchase with nothing shown to the customer.
      throw reportIndeterminateOrdwayPurchase(
        rail,
        `Ordway ${rail.label} purchase run ${runId} lost its account while polling`,
      );
    }
    const accountId = String(account.id);
    const tracker = createOrdwayPollTracker(rail, runId);

    for (let attempt = 0; attempt < ORDWAY_PURCHASE_POLL_MAX_ATTEMPTS; attempt++) {
      if (signal.aborted) {
        return false;
      }

      const read = await readStatus(accountId, runId);
      if (readOrdwayAttempt(rail, read, runId, tracker.notFoundStreak)) {
        return true;
      }
      tracker.record(read);

      await pollDelay(ORDWAY_PURCHASE_POLL_INTERVAL_MS, signal);
    }

    // Giving up here says nothing about whether the card was charged: the workflow's pre-charge steps
    // alone are budgeted well past this poll's deadline. Report the unknown outcome rather than a
    // failure, and never prompt a retry that could double-charge.
    throw reportIndeterminateOrdwayPurchase(rail, tracker.timedOutDiagnostic(ORDWAY_PURCHASE_POLL_MAX_ATTEMPTS));
  });
}

export const billing = {
  isOrdwayCustomer,
  isOrdwayCustomerWaitingInitialization,
  isChurnIncluded,
  isVerificationRejected,

  // Who bills this account. Not the same as `isOrdwayCustomer` or `isStripeCustomer`, which ask whether a
  // record exists in that system. Read the note on their definitions first.
  isLegacyStripeVendor,
  isOrdwayVendor,
  isMetronomeVendor,

  /** The session is a read-only masquerade. The permission checks also stop edits. */
  isMasqReadOnly: isMasqReadOnlyState,

  initialized,
  loadFailed,
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  initializedAt: null as number | null,

  subscription,
  coupon,
  taxRate,
  paramPlan,
  billingInfo,
  plans,
  tiers,
  customer,
  futureSubscription,
  products,
  usage,
  availablePlans,

  isPremiumTrial,
  isFreeTrialPlan,
  isPendingCheckout,
  isTrial,
  pinnedPlan,
  essentialsPlan,
  billingPeriodStart,
  billingPeriodEnd,
  nextChargeDate,
  isCouponActiveForNextCharge,
  includedProfiles,
  includedMessages,
  includedObjects,
  includedMau,
  totalCustomerCount,
  isUpgradeDisabled,
  upgradeDisabledMessage,
  disabledMessageForPermission,

  planType,
  futurePlanType,
  planChange,
  planSlug,
  isBasic,
  isPremium,
  isPremiumOrAbove,
  isBasicTrial,
  planNameShortened,
  planDisplayName,
  isBuilder,
  isBuilderPlan,
  isFreeBuilder,
  isFlex,
  usesCredits,
  upgradePlanTypes,

  // Subscription lifecycle and date derivations.
  isSubscriptionActive,
  pinnedStartDate,
  formattedStartDate,
  baseBillingCadence,
  isBilledMonthly,
  isOverageOnlyCycle,
  isOverageOnlyNextCharge,
  contractTermStart,
  contractTermEnd,
  contractRenewalDate,
  contractRenewsAutomatically,
  isLumpSumPlan,
  cancelsAt,
  canCancel,
  isEligibleForBuilderDowngrade,

  // Pricing, rate and summary derivations.
  planPrice,
  meteredCustomerRate,
  meteredObjectRate,
  meteredMessageRate,
  meteredMauRate,
  isMauPricing,
  intervalDisplayName,
  cdpPremiumPrice,
  couponEndsAt,
  usageSyncedAt,
  usageResetsAt,
  billingEstimates,
  reportsWholeBill,
  isEligibleForRetentionDiscount: retentionDiscountEligible,

  // Payment-method and charge-status derivations.
  hasCard,
  hasAchSetup,
  hasFailedOverageCharge,
  stripeCustomer,
  isStripeCustomer,
  canRetryPayment,
  lastPaymentError,
  /** The `card` atom: the account's saved card as a `PaymentCard`. */
  cardDetails: card,

  /** The gateway for the tax-rate writers in the embedded apps. */
  setTaxRate(value?: TaxRate) {
    taxRate.set(value);
  },

  resetTaxRate() {
    taxRate.set(undefined);
  },

  /** Called by the embedded app after it fetches the Ordway data. */
  setOrdwayBillingEstimate(value?: BillingEstimates) {
    ordwayBillingEstimate.set(value);
  },

  setRetentionDiscountEligible(value: boolean) {
    retentionDiscountEligible.set(value);
  },

  /**
   * Sets each account-scoped atom back to its initial value, which gives a new account a clean slate. Call
   * it on an in-app account switch, such as a masquerade, before the service initializes again. A page
   * reload discards these module-level atoms, but an in-app switch keeps them, so the data of the previous
   * account can leak into the new one. This includes `availablePlans`, `ordwayBillingEstimate`,
   * `retentionDiscountEligible` and `usage`, which no refresh path writes. It resets all of the atoms, and
   * not a selection of them, to stay correct as the service grows. A clear `initialized` lets the next
   * `initialize.perform` run fully.
   */
  handleAccountSwitch() {
    subscription.set(undefined);
    coupon.set(undefined);
    taxRate.set(undefined);
    paramPlan.set(null);
    billingInfo.set(undefined);
    plans.set(undefined);
    tiers.set(undefined);
    customer.set(undefined);
    futureSubscription.set(undefined);
    products.set(undefined);
    usage.set(undefined);
    availablePlans.set(undefined);
    ordwayBillingEstimate.set(undefined);
    card.set(null);
    retentionDiscountEligible.set(false);
    initialized.set(false);
    loadFailed.set(false);
    billing.initializedAt = null;
  },

  /**
   * The subscription start date is in the future. It is a getter, and not a computed, because the Stripe
   * branch compares the pinned start date to the current time.
   */
  get startDateInFuture(): boolean {
    const sub = subscription.get();
    if (isMetronomeSubscription(sub)) {
      return false;
    }
    if (isOrdwaySubscription(sub)) {
      return futureSubscription.get() != null;
    }

    // This is not an Ordway account, so read `billingInfo.pinned_start_date` directly.
    return (billingInfo.get()?.pinned_start_date ?? 0) > Math.floor(Date.now() / 1000);
  },

  // Date-based trial derivations. They are getters, and not computeds, because they depend on the current
  // time. Each read compares against "now" again.
  /** False for a subscription that is not a Stripe subscription. */
  get isNextBillingWithinAWeek(): boolean {
    const sub = subscription.get();
    if (!isStripeSubscription(sub)) {
      return false;
    }

    const periodEnd = billingPeriodEnd.get();
    const ONE_WEEK_IN_SECONDS = 7 * 24 * 60 * 60;
    return periodEnd != null && periodEnd < Math.floor(Date.now() / 1000) + ONE_WEEK_IN_SECONDS;
  },

  /** When the trial expires, as unix seconds. */
  get trialExpiresAt(): number | null {
    const sub = subscription.get();
    if (isMetronomeSubscription(sub)) {
      return sub.contract_end != null && sub.contract_end > 0 ? sub.contract_end : null;
    }
    if (isOrdwaySubscription(sub)) {
      return utcStartOfDayUnix(sub.billing_start_date);
    }

    const account = accountStore.get();
    if (isTrial.get()) {
      return account?.suspend_trial_at ?? null;
    }

    return account?.planFeatures.messaging.expiresAt ?? null;
  },

  get daysLeftInTrial(): number {
    const expiresAt = this.trialExpiresAt;
    if (expiresAt == null) {
      return 0;
    }

    // The count of full 24-hour periods between the two instants, and not a count of calendar days. Thus
    // 2 days and 23 hours gives "2 days left", not 3. Neither value is a calendar date, so time-zone
    // arithmetic is not necessary.
    return Math.trunc((expiresAt * 1000 - Date.now()) / (24 * 60 * 60 * 1000));
  },

  get trialExpired(): boolean {
    if (!isTrial.get()) {
      return false;
    }
    const sub = subscription.get();
    if (isMetronomeSubscription(sub)) {
      return positiveOrUndefined(sub.contract_end) != null && sub.contract_end! * 1000 < Date.now();
    }
    if (isOrdwaySubscription(sub)) {
      return (
        accountStore.get()?.account_state !== AccountState.Current &&
        utcStartOfDayUnix(sub.billing_start_date) * 1000 < Date.now()
      );
    }
    return isStripeTrialExpired(accountStore.get());
  },

  /** A getter, and not a computed, because the Ordway branch reads the time-based {@link billing.trialExpired}. */
  get isTrialSuspended(): boolean {
    if (isMetronomeVendor.get()) {
      return accountStore.get()?.account_state === AccountState.Suspended && this.trialExpired;
    }
    if (isOrdwayCustomer.get()) {
      return isOrdwayTrialSuspended(this.trialExpired);
    }
    return billingInfo.get()?.trial_suspended ?? false;
  },

  /**
   * Whether the account's condition allows offering the self-serve Reactivate flow. Only half the
   * answer: `can_reactivate` carries the policy half (breach reason, reactivation limit), and the
   * caller ANDs the two.
   *
   * Strictly widens {@link billing.isTrialSuspended}, the operand it replaces, so no account offered the
   * flow today loses it. The one addition is the cancelled non-Ordway case, which
   * `billing_info.trial_suspended` cannot express: cancelling nulls `suspend_trial_at` and leaves the
   * state `canceled` rather than `suspended` (`services/domains/accounts/update.go:312`), so the flag is
   * always `false` once an account is cancelled. Ordway reads `account_state` directly and already
   * covers it, so widening there would only admit cancelled accounts whose subscription the reactivate
   * endpoint still refuses.
   *
   * Branches on {@link isOrdwayVendor} rather than {@link isOrdwayCustomer} so that a Metronome-billed
   * account holding a stale `ordway_id` is judged by who bills it, matching the backend's vendor dispatch
   * in `reactivateGeneric`.
   *
   * A getter, and not a computed, because it reads the time-based {@link billing.isTrialSuspended}.
   */
  get accountStateAllowsReactivate(): boolean {
    if (isOrdwayVendor.get()) {
      return this.isTrialSuspended;
    }

    return this.isTrialSuspended || accountStore.get()?.account_state === AccountState.Canceled;
  },

  /** A getter because it reads the time-based {@link billing.daysLeftInTrial}. */
  get trialSuspendingSoon(): boolean {
    return this.daysLeftInTrial <= 3 && accountStore.get()?.account_state === AccountState.Free;
  },

  /**
   * The plan name, with a trial or past-due suffix when necessary. A getter because it reads the
   * time-based {@link billing.isTrialSuspended} and {@link billing.trialSuspendingSoon}.
   */
  get planName(): string {
    const nameShort = planNameShortened.get();

    if (isTrial.get()) {
      if (this.trialSuspendingSoon) {
        return `${nameShort} Expiring Soon`;
      }
      if (this.isTrialSuspended) {
        return `${nameShort} Expired`;
      }
    } else if (accountStore.get()?.account_state === AccountState.Overdue) {
      return `${nameShort} Past Due`;
    }

    return nameShort;
  },

  /**
   * The time left in the trial, relative to now, e.g. "in 3 days" or "3 days ago". A getter because each
   * read must compare against "now" again. Pluma's `formatRelativeTime` compares against now by default
   * and adds the "in" or "ago" direction. `absoluteFormatDiffCutoff: null` stops its change to an absolute
   * date after 30 days, because a trial date more than a month away must stay relative.
   */
  get formattedTimeLeftInTrial(): string {
    const sub = subscription.get();
    if (isMetronomeSubscription(sub)) {
      const contractEnd = sub.contract_end;
      if (contractEnd == null || contractEnd === 0) {
        return '';
      }
      return formatRelativeTime(contractEnd, new Date(), {
        absoluteFormatDiffCutoff: null,
        shouldUppercaseFirst: false,
      });
    }
    if (isOrdwaySubscription(sub)) {
      return formatRelativeTime(utcStartOfDayUnix(sub.billing_start_date), new Date(), {
        absoluteFormatDiffCutoff: null,
        shouldUppercaseFirst: false,
      });
    }

    // A missing `suspend_trial_at` would anchor the relative time to 1970. A Stripe trial usually has one,
    // so only sparse data gets here. Show nothing instead of a wrong date.
    const suspendTrialAt = accountStore.get()?.suspend_trial_at;
    if (suspendTrialAt == null) {
      return '';
    }
    return formatRelativeTime(suspendTrialAt, new Date(), {
      absoluteFormatDiffCutoff: null,
      shouldUppercaseFirst: false,
    });
  },

  /**
   * A summary of the billing status for the user. A getter because it reads the time-based
   * {@link billing.formattedTimeLeftInTrial} and {@link billing.daysLeftInTrial}.
   */
  get planSummary(): string {
    const account = accountStore.get();

    if (account?.account_state === AccountState.Overdue) {
      return 'Your account is overdue.';
    }
    if (this.isTrialSuspended) {
      // `daysLeftInTrial` is negative after the trial ends. Pluralize on the same absolute value that the
      // user sees, to get "ended 1 day ago" and not "ended 1 days ago".
      const daysSinceTrialEnded = Math.abs(this.daysLeftInTrial);
      return `Your trial ended ${daysSinceTrialEnded} ${plur('day', daysSinceTrialEnded)} ago.`;
    }
    if (isPendingCheckout.get()) {
      return `Your trial will automatically upgrade ${this.formattedTimeLeftInTrial}.`;
    }
    if (isTrial.get()) {
      return `Your trial expires ${this.formattedTimeLeftInTrial}.`;
    }
    return `Your account is ${account?.account_state}.`;
  },

  /**
   * `res` is `Partial` on purpose: every read below is `?? []` / `?? undefined`,
   * so a response missing a section is handled rather than assumed, and a caller
   * (or a test) can hand over just the sections it has.
   */
  setStripeBillingProperties(res: Partial<BillingGetInfoResponse>, paramPlanSlug: string) {
    const context = stripePlanContext(res.billing_info);
    const stripePlans = (res.plans ?? []).map((plan) => stripePlanToBillingPlan(plan, context));

    this.billingInfo.set(res.billing_info ?? undefined);
    this.subscription.set(
      res.subscription == null ? undefined : stripeSubscriptionToBillingData(res.subscription, stripePlans),
    );
    this.coupon.set(res.coupon ?? undefined);
    this.plans.set(stripePlans);
    this.tiers.set(res.tiers ?? []);
    this.taxRate.set(res.tax_rate ?? undefined);
    this.paramPlan.set(
      isEmptyString(paramPlanSlug) ? null : (stripePlans.find((plan) => plan.slug === paramPlanSlug) ?? null),
    );
    card.set(stripeSubscriptionCard(res.subscription));
    // Clear the Ordway data, which is now stale.
    this.customer.set(undefined);
    this.futureSubscription.set(undefined);
    this.products.set(undefined);
  },

  /** `res` is `Partial` for the same reason as {@link setStripeBillingProperties}. */
  // fallow-ignore-next-line complexity -- inherent to the billing domain mapping
  setOrdwayBillingProperties(res: Partial<BillingOrdwayProfileResponse>) {
    const { effective, upcoming } = resolveOrdwaySubscriptions(res.ordway_subscriptions ?? []);

    this.customer.set(res.ordway_customer ?? undefined);
    this.subscription.set(effective == null ? undefined : ordwaySubscriptionToBillingPlan(effective));
    this.futureSubscription.set(upcoming == null ? undefined : ordwaySubscriptionToBillingPlan(upcoming));
    this.coupon.set(res.coupon ?? undefined);
    this.products.set(effective?.plans ?? []);
    // Clear the legacy Stripe data, which is now stale.
    this.billingInfo.set(undefined);
    this.plans.set(undefined);
    this.tiers.set(undefined);
    // `fetchOrdwayCardDetails` loads the Ordway card, so this response has no card details and cannot
    // refresh them. But `has_credit_card` does tell you if a card exists. Clear the card only when it says
    // there is none, which also removes a card from another vendor or account. If you always clear it, each
    // new read of the profile removes the details of a card that is still on file.
    if (res.ordway_customer?.has_credit_card !== true) {
      card.set(null);
    }
  },

  setMetronomeBillingProperties(profile: BillingMetronomeProfileResponse) {
    this.subscription.set(metronomeProfileToBillingPlan(profile));
    if (profile.payment != null) {
      card.set({
        last4: profile.payment.card_last4,
        brand: profile.payment.card_brand,
        expiry: profile.payment.card_expiry,
        ownerName: profile.payment.card_owner_name,
      });
    } else {
      card.set(null);
    }
    this.billingInfo.set(undefined);
    this.customer.set(undefined);
    this.futureSubscription.set(undefined);
    this.products.set(undefined);
  },

  /** Fetches the tax rate and stores it. */
  updateTaxRate: enqueueTask(
    async ({
      country,
      state,
      zipCode,
      addressLine1,
      signal,
    }: {
      country: string;
      state: string;
      zipCode: string;
      addressLine1?: string;
      /**
       * Aborts the *write*, and not only the request. `enqueueTask` is serial and cannot cancel, so a run
       * that a newer address change supersedes still gets here, and would write its rate over the newer
       * one. This task does the write, so the caller cannot undo it. It must stop here.
       */
      signal?: AbortSignal;
    }): Promise<void> => {
      const account = accountStore.get();
      if (account == null || isSuperseded(signal)) {
        return;
      }

      try {
        // Ordway (Avalara) keys the lookup by region and street. Stripe (TaxJar) keys it by state. Test the
        // account `ordway_id`, as the other Ordway paths do, and not the subscription. An Ordway account can
        // get here before its subscription loads, e.g. early in checkout.
        const query = isOrdwayCustomer.get()
          ? { country, zip_code: zipCode, region: state, address1: addressLine1 }
          : { country, zip_code: zipCode, state };

        const res = await hydraUiApi.billing.getTaxRate({
          params: { path: { account_id: String(account.id) }, query },
        });
        if (isSuperseded(signal)) {
          return;
        }
        billing.setTaxRate(res.tax_rate);
      } catch (error) {
        if (isSuperseded(signal)) {
          return;
        }
        // A failed tax lookup must not stop the flow. Report it and clear the stale rate.
        sentryCaptureException(error);
        billing.resetTaxRate();
      }
    },
  ),

  /** Fetches a Stripe setup intent, to add or to change a card. */
  getSetupIntent: enqueueTask(async () => {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot create a setup intent without an account');
    }

    return hydraUiApi.billing.getSetupIntent({ params: { path: { account_id: String(account.id) } } });
  }),

  /**
   * Fetches the Ordway card and payment details.
   *
   * It must enqueue, and not drop, concurrent calls. It is the `queryFn` of three independent React Query
   * callers: payment methods, the credits-purchase form and the reactivate-modal loader. A dropped read
   * resolves `undefined`, which React Query caches as the answer, so a second page that mounts at the same
   * time shows "no card on file" for an account that has one. Dropping is correct for a write, but wrong
   * for a read.
   */
  fetchOrdwayCardDetails: enqueueTask(async () => {
    const account = accountStore.get();
    if (account == null) {
      return undefined;
    }

    // Store the card in the `card` atom, which `cardDetails` reads, and also return the raw payment method
    // for the embedded card-details component, which reads it directly.
    const payment = await hydraUiApi.billing.getPayment({ params: { path: { account_id: String(account.id) } } });
    // A missing credit card in Ordway causes the response to be null
    if (payment == null) {
      return payment;
    }
    card.set(ordwayPaymentToPaymentCard(payment));
    return payment;
  }),

  /**
   * Loads the plans catalog into `availablePlans` (Ordway) or `plans` (Metronome). A Stripe account's
   * plans arrive with `billing/info` during `initialize`, so this is a no-op for Stripe. A Metronome
   * account loads the Stripe plan catalog on demand because `initialize` only fetches the Metronome
   * profile.
   */
  fetchAvailablePlans: enqueueTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    if (isOrdwayCustomer.get()) {
      const res = await hydraUiApi.billing.getPlans({
        params: { path: { account_id: String(account.id) }, query: { product_type: 'journeys' } },
      });
      availablePlans.set((res.ordwayPlans ?? []).map(ordwayPlanToBillingPlan));
      return;
    }

    if (isMetronomeVendor.get() && plans.get() == null) {
      const res = await loadStripeInfo(account.id, false);
      const context = stripePlanContext(res.billing_info);
      plans.set((res.plans ?? []).map((plan) => stripePlanToBillingPlan(plan, context)));
      tiers.set(res.tiers ?? []);
    }
  }),

  /**
   * Checks retention-discount eligibility and stores it. Only an Essentials account that is not on a trial
   * can be eligible. All other accounts get false.
   *
   * It must enqueue, and not drop, concurrent calls. It is a read that sets eligibility to false before it
   * fetches, so a dropped call leaves the cancel flow with `false`, and the user does not get a discount
   * they are entitled to. Serial calls cost one more request. A dropped call costs the retention offer.
   */
  checkRetentionDiscountEligibility: enqueueTask(async (): Promise<void> => {
    billing.setRetentionDiscountEligible(false);

    if (planType.get() !== PlanType.Essentials || isTrial.get()) {
      return;
    }

    const account = accountStore.get();
    if (account == null) {
      return;
    }

    try {
      const res = await hydraUiApi.billing.getRetentionDiscount({
        params: { path: { account_id: String(account.id) } },
      });
      billing.setRetentionDiscountEligible(res.eligible === true);
    } catch {
      billing.setRetentionDiscountEligible(false);
    }
  }),

  /**
   * Waits for an `account_state` change that a Temporal workflow applies after the request has already
   * returned, then reloads billing once so the page agrees with the outcome it reported.
   *
   * The Metronome cancel and reactivate endpoints answer as soon as their workflow is *enqueued*; the
   * state lands roughly a second later (`applyPaidCancellation` / its reactivation counterpart). A
   * reload issued straight after the mutation therefore reads the pre-mutation state, and the page
   * contradicts the snackbar — the Cancel CTA stays live and Reactivate never appears.
   *
   * Two things are deliberate:
   * - It reads the account endpoint directly rather than through `fetchAccountById`, whose cross-app
   *   cache would hand back the same stale payload for its full TTL no matter how long we waited.
   * - It drops the cached account keys before reloading, because the auth refetch that
   *   `announceRefreshAuth` triggers reads that cache too — without this the poll would observe the new
   *   state and the UI would still render the old one.
   *
   * Ordway writes its state inside the request, so `hasSettled` is already true on the first read and
   * this returns after one call. A workflow that never lands is bounded by the attempt cap: the reload
   * still runs, leaving the page no staler than it would have been with no wait at all.
   */
  pollForAccountStateTask: pollTask(async (signal, hasSettled: (accountState: string) => boolean): Promise<void> => {
    const account = accountStore.get();

    if (account != null) {
      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        if (signal.aborted) {
          return;
        }

        const res = await hydraUiApi.accounts.get({
          params: { path: { account_id: String(account.id) } },
          signal,
        });

        // A payload with no account is not a settled state — keep waiting rather than reloading on it.
        if (res.account != null && hasSettled(res.account.account_state)) {
          break;
        }

        await pollDelay(POLL_INTERVAL_MS, signal);
      }

      deleteCrossAppCacheKeys('/accounts/current', `/accounts/${account.id}`);
      deleteCrossAppCacheByPrefix('/account_by_environment');
    }

    await billing.initialize.perform({ refresh: true, includeCoupons: true });
  }),

  /**
   * Polls `billing/subscription_{complete,exists}` until the subscription change completes. It resolves on
   * success, throws the server error on failure, and throws an empty error on a timeout. `cancelAll` stops
   * it.
   */
  pollForSubscriptionTask: pollTask(async (signal, type: 'complete' | 'exists'): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }
    const accountId = String(account.id);

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      if (signal.aborted) {
        return;
      }

      let setupError = '';
      try {
        const res =
          type === 'complete'
            ? await hydraUiApi.billing.getSubscriptionComplete({ params: { path: { account_id: accountId } } })
            : await hydraUiApi.billing.getSubscriptionExists({ params: { path: { account_id: accountId } } });
        if (isEmptyString(res.error)) {
          return;
        }
        setupError = res.error;
      } catch {
        // The request failed. Try again.
      }

      if (!isEmptyString(setupError)) {
        throw new Error(setupError);
      }

      await pollDelay(POLL_INTERVAL_MS, signal);
    }

    // No success response after the maximum attempts. An empty error is enough to show the timeout.
    throw new Error();
  }),

  pollForOrdwayCheckoutTask,

  /**
   * Polls `billing/{ai_credits,builder_credits}/purchase_status` until the credits purchase is granted. It
   * resolves `true` for a granted purchase, and `false` when it stops before it can confirm one, i.e.
   * `cancelAll` stopped it or there is no active account. It throws on a failure or a timeout. A caller must
   * read a result that is not `true` as "not granted", and must not report success.
   */
  pollForCreditsPurchaseTask: pollTask(
    // fallow-ignore-next-line complexity -- inherent to the status handling of the poll
    async (
      signal,
      creditType: 'ai_credits' | 'builder_credits' | 'inbox_previews',
      invoiceId: string,
    ): Promise<boolean> => {
      const account = accountStore.get();
      if (account == null) {
        return false;
      }
      const accountId = String(account.id);
      const label =
        creditType === 'ai_credits' ? 'AI credits' : creditType === 'inbox_previews' ? 'Inbox Previews' : 'Added funds';
      let lastStatus: string | undefined;

      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        if (signal.aborted) {
          return false;
        }

        let status: string | undefined;
        try {
          const statusParams = { path: { account_id: accountId }, query: { invoice_id: invoiceId } };
          const res =
            creditType === 'ai_credits'
              ? await hydraUiApi.billing.getAiCreditsPurchaseStatus({ params: statusParams })
              : creditType === 'inbox_previews'
                ? await hydraUiApi.billing.getInboxPreviewsPurchaseStatus({ params: statusParams })
                : await hydraUiApi.billing.getBuilderCreditsPurchaseStatus({ params: statusParams });
          status = res.status;
        } catch {
          // Request failed; retry.
        }

        if (status === 'granted') {
          return true;
        }
        if (status === 'failed') {
          throw new Error('Your payment failed. Contact support.');
        }
        if (status != null) {
          lastStatus = status;
        }

        await pollDelay(POLL_INTERVAL_MS, signal);
      }

      if (lastStatus === 'processing') {
        throw new Error('Your payment was received and is still being processed. Your credits will appear shortly.');
      }

      sentryCaptureException(
        new Error(
          `${label} purchase polling timed out after ${POLL_MAX_ATTEMPTS} attempts for invoice ${invoiceId} (lastStatus: ${lastStatus ?? 'none'})`,
        ),
      );
      throw new Error("Your purchase couldn't be processed. Contact support.");
    },
  ),

  /**
   * Polls `billing/ai_credits/ordway_purchase_status` until the Ordway purchase workflow identified by
   * `runId` reaches a terminal state. See {@link ordwayPurchasePollTask} for the shared semantics.
   *
   * Unlike {@link pollForCreditsPurchaseTask}, losing the account throws rather than resolving `false`:
   * that sibling polls a charge the client already confirmed, whereas here the workflow is running and
   * `false` — indistinguishable from a cancelled poll — would leave the purchase with no outcome at all.
   *
   * Separate from {@link pollForCreditsPurchaseTask} rather than a mode of it: the Stripe path keys on
   * an invoice id and terminates on `granted`, while Ordway keys on a Temporal run id, terminates on
   * `success`, and adds the `declined` outcome.
   */
  pollForOrdwayAICreditsPurchaseTask: ordwayPurchasePollTask(ORDWAY_AI_CREDITS_RAIL, (accountId, runId) =>
    readOrdwayPurchaseStatus(() =>
      hydraUiApi.billing.getAiCreditsOrdwayPurchaseStatus({
        params: { path: { account_id: accountId }, query: { run_id: runId } },
      }),
    ),
  ),

  /**
   * Polls `billing/inbox_previews/ordway_purchase_status` until the Ordway purchase workflow identified
   * by `runId` reaches a terminal state. Same semantics as
   * {@link pollForOrdwayAICreditsPurchaseTask} — see {@link ordwayPurchasePollTask} — on the previews
   * rail's own endpoint, and with its own cancellation scope.
   */
  pollForOrdwayInboxPreviewsPurchaseTask: ordwayPurchasePollTask(ORDWAY_INBOX_PREVIEWS_RAIL, (accountId, runId) =>
    readOrdwayPurchaseStatus(() =>
      hydraUiApi.billing.getInboxPreviewsOrdwayPurchaseStatus({
        params: { path: { account_id: accountId }, query: { run_id: runId } },
      }),
    ),
  ),

  // Subscription and account mutation tasks. Each one does only the billing REST mutation, and the
  // validation of the response where that is necessary. It resolves on success, or throws the
  // `HydraUIAPIError` on failure. The caller keeps the related work: the snackbars, the router
  // transitions, the account-state refresh, and the navigation.

  /**
   * Pauses the subscription. The caller shows the snackbars.
   *
   * **It returns whether it sent the request**, so the caller can tell a refusal from an accepted pause. If
   * the caller reads a bare resolve as success, it tells the user about a pause that did not occur. The
   * caller must read the result before it shows a success snackbar.
   *
   * Only a legacy Stripe account can pause. The backend rejects each other vendor with a 400
   * (`services/ui_api/billing/pause.go:66`), so this task refuses instead. The UI also hides Pause plan for
   * those vendors (`deriveShouldShowPausePlan`), and the caller checks again before it opens its modal. Thus
   * a refusal here means that both of those checks were bypassed or are stale, e.g. an account switch while
   * the modal is open, or a caller that does not use the menu.
   *
   * It refuses instead of a throw, so the user does not see a failure. Nothing went wrong: the action is
   * only not applicable to this account.
   *
   * `unpauseSubscription` has no equivalent guard. An account can be paused only if legacy Stripe billed it
   * at that time, so the check is unreachable, and a wrong check would keep the account paused.
   */
  pauseSubscription: coalesceTask(async (feedback: string): Promise<boolean> => {
    const account = accountStore.get();
    if (account == null) {
      return false;
    }
    if (!isLegacyStripeVendor.get()) {
      return false;
    }

    const body: BillingPauseRequest = { action: 'pause', feedback };
    await hydraUiApi.billing.subscriptionsPause({ params: { path: { account_id: String(account.id) } }, body });
    return true;
  }),

  /**
   * Unpauses (reactivates) the subscription. The caller shows the snackbars. The pause endpoint requires
   * `feedback` in the body, but unpause has none, so send an empty string. The server ignores it.
   */
  unpauseSubscription: coalesceTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const body: BillingPauseRequest = { action: 'unpause', feedback: '' };
    await hydraUiApi.billing.subscriptionsPause({ params: { path: { account_id: String(account.id) } }, body });
  }),

  /**
   * Reactivates a subscription that is pending cancellation, with the account `reactivate` endpoint. The
   * endpoint takes no body. The caller shows the snackbars.
   */
  reactivateSubscription: coalesceTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.accounts.reactivate({ params: { path: { account_id: String(account.id) } } });
  }),

  /**
   * Reactivates a canceled account as suspended, so the user can pay and use the app again. A different
   * endpoint from {@link reactivateSubscription}, which needs a subscription that is still active.
   *
   * On Ordway, this returns before the account state changes, so the caller must then use
   * {@link pollForAccountReactivationTask}. The caller shows the messages.
   */
  reactivateAsSuspended: coalesceTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.accounts.reactivateAsSuspended({ params: { path: { account_id: String(account.id) } } });
  }),

  /**
   * Waits until the Ordway workflow moves the account out of the canceled state. Gives `true` after the
   * state changes, and throws an empty error after a timeout.
   *
   * Gives `false` if it stops before it can confirm the change. A cancel gives `undefined`, so the caller
   * must accept only `true` as a success.
   *
   * It reads the account endpoint directly, because the usual helper gives a cached answer. After a
   * success it clears that cache, so the other embedded apps do not read the old state.
   */
  pollForAccountReactivationTask: pollTask(async (signal): Promise<boolean> => {
    const account = accountStore.get();
    if (account == null) {
      return false;
    }
    const accountId = String(account.id);

    for (let attempt = 0; attempt < REACTIVATION_POLL_MAX_ATTEMPTS; attempt++) {
      if (signal.aborted) {
        return false;
      }

      try {
        const res = await hydraUiApi.accounts.get({ params: { path: { account_id: accountId } } });

        // Test again, because the request does not receive the signal. A cancel during the request must
        // stop this run, or the caller reads a success and moves a user who left the page.
        if (signal.aborted) {
          return false;
        }

        const state = res.account?.account_state;
        // A response without an account gives no answer, so wait for one that has an account.
        if (state != null && state !== AccountState.Canceled) {
          deleteCrossAppCacheByPrefix('/accounts');
          return true;
        }
      } catch {
        // The request failed. Try again.
      }

      await pollDelay(REACTIVATION_POLL_INTERVAL_MS, signal);
    }

    // Still canceled after the last attempt. An empty error is sufficient for a timeout.
    throw new Error();
  }),

  /**
   * Applies the 50%-off retention discount. The caller reloads the billing data, shows the snackbar, and
   * reads `HydraUIAPIError.status` to tell an already-redeemed discount (HTTP 409) from other errors.
   */
  applyRetentionDiscount: coalesceTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.billing.createRetentionDiscount({ params: { path: { account_id: String(account.id) } } });
  }),

  /**
   * Downgrades the account to the free Builder plan. The caller shows the snackbar and then reloads the page.
   * The endpoint needs an empty JSON object, because its route rejects an empty body.
   */
  downgradeToBuilder: coalesceTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.billing.downgradeToBuilder({ params: { path: { account_id: String(account.id) } }, body: {} });
  }),

  /**
   * Cancels the Ordway subscription with the `billing/subscriptions/cancel` endpoint. Ordway only — the
   * endpoint returns 200 and does nothing at all for any other vendor
   * (`ui_api/billing/subscribe.go:628`), so a Metronome or legacy-Stripe cancel sent here looks like it
   * worked and never happens. Those use `cancelStripeSubscription` below. The caller keeps the
   * snackbars, the feedback delay and the navigation.
   */
  // The two vendor cancel endpoints have the same simple request shape. A shared wrapper would hide more than
  // it saves.
  // fallow-ignore-next-line code-duplication
  cancelOrdwaySubscription: coalesceTask(async (feedback: string, switchingTo: string): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const body: BillingCancelOrdwaySubscriptionRequest = { feedback, switching_to: switchingTo };
    await hydraUiApi.billing.subscriptionsCancel({ params: { path: { account_id: String(account.id) } }, body });
  }),

  /**
   * Cancels the account with the generic `accounts/{id}/cancel` endpoint, which serves both Metronome and
   * legacy Stripe. Whether that closes the account now or schedules it for the end of the billing period
   * is the endpoint's call, not this one's — see `resolveCancelTiming`. The caller keeps the confirm
   * prompt, the feedback delay, the snackbars and the navigation. `cancelOrdwaySubscription` above is the
   * Ordway path.
   *
   * **The `Stripe` in the name is now wrong, and stays anyway.** This whole object is
   * `window.__hydra__.billing`, and Ember calls this method through it — including its own Metronome
   * branch (`ui` `app/services/billing.ts:1036`, mirrored in `app/utils/billing/hydra.ts:396`). Renaming
   * it here alone is a cross-repo break that only shows up as a TypeError when a user clicks cancel, so
   * it needs a coordinated change on both sides.
   */
  cancelStripeSubscription: coalesceTask(async (feedback: string, switchingTo: string): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const body: BillingCancelRequest = { feedback, switching_to: switchingTo };
    await hydraUiApi.billing.cancel({ params: { path: { account_id: String(account.id) } }, body });
  }),

  /**
   * Activates a draft Ordway subscription. It throws when the server does not report the subscription Active,
   * so the caller can treat that as a failure. The caller refreshes the account state, shows the snackbar,
   * makes the router transition, and reports to Sentry.
   */
  activateSubscriptionInOrdway: coalesceTask(async (subscriptionId: string): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const res = await hydraUiApi.billing.subscriptionsActivate({
      params: { path: { account_id: String(account.id), subscription_id: subscriptionId } },
    });

    const activated = res.ordway_subscriptions?.[0]?.status === 'Active';
    if (!activated) {
      throw new Error('Subscription activation failed, no active subscription found');
    }
  }),

  /**
   * Subscribes the account to an Ordway plan. The caller passes the billing-contact fields as the request
   * body, refreshes the account state, shows the snackbar, and makes the router transition.
   */
  subscribeToPlanInOrdway: coalesceTask(
    async (planId: string, contact: BillingOrdwaySubscriptionRequest): Promise<void> => {
      const account = accountStore.get();
      if (account == null) {
        return;
      }

      await hydraUiApi.billing.createPlans({
        params: { path: { account_id: String(account.id), plan_id: planId } },
        body: contact,
      });
    },
  ),

  /**
   * Changes the card of the account from a confirmed Stripe SetupIntent. The caller keeps the snackbars, and,
   * for a Stripe account, the subscription poll and the refresh.
   */
  updateCard: coalesceTask(async (setupIntent?: SetupIntentPayload): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.billing.card({
      params: { path: { account_id: String(account.id) } },
      body: { setup_intent: setupIntent },
    });
  }),

  /**
   * Changes the billing profile of the Ordway customer. The credit-card form calls it when it saves the
   * address of an Ordway account. A Stripe token, when there is one, links the confirmed card to the Ordway
   * customer.
   */
  async updateOrdwayCustomerBillingDetails(
    contact: Omit<BillingUpdateOrdwayProfileRequest, 'stripe_token'>,
    stripeToken?: string,
  ): Promise<void> {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.billing.updateProfile({
      params: { path: { account_id: String(account.id) } },
      body: { ...contact, stripe_token: stripeToken ?? '' },
    });
  },

  /**
   * Subscribes the account to a Stripe plan from a confirmed SetupIntent. The caller owns the plan and coupon
   * selection, the subscription poll, the account-state refresh, the snackbars and the router transition. The
   * request does not send `annual`, because the server gets that from the plan.
   */
  startSubscription: coalesceTask(
    async (planId: string | undefined, couponCode: string, setupIntent?: SetupIntentPayload): Promise<void> => {
      const account = accountStore.get();
      if (account == null) {
        return;
      }

      await hydraUiApi.billing.subscribe({
        params: { path: { account_id: String(account.id) } },
        body: { plan_id: Number(planId), coupon_code: couponCode, setup_intent: setupIntent },
      });
    },
  ),

  /**
   * Creates a Stripe PaymentIntent for a credits purchase, and returns its client secret and invoice id. AI
   * credits and builder credits have different request bodies, because only builder credits accepts
   * `save_card`, so each one uses its own typed endpoint.
   */
  async createCreditsPaymentIntent(
    creditType: 'ai_credits' | 'builder_credits' | 'inbox_previews',
    quantity: number,
    address: { country: string; state: string; city: string; zipCode: string; street: string },
    useSavedCard = false,
    saveCard = false,
  ): Promise<{ client_secret: string; invoice_id: string }> {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot create a credits payment intent without an account');
    }

    const params = { path: { account_id: String(account.id) } };

    if (creditType === 'inbox_previews') {
      // The pack is named by its preview count; the server maps it to a price.
      return hydraUiApi.billing.inboxPreviewsPaymentIntent({
        params,
        body: {
          package: quantity,
          country: address.country,
          state: address.state,
          city: address.city,
          zip_code: address.zipCode,
          street: address.street,
          use_saved_card: useSavedCard,
        },
      });
    }

    if (creditType === 'ai_credits') {
      return hydraUiApi.billing.aiCreditsPaymentIntent({
        params,
        body: {
          quantity,
          country: address.country,
          state: address.state,
          city: address.city,
          zip_code: address.zipCode,
          street: address.street,
          use_saved_card: useSavedCard,
        },
      });
    }

    return hydraUiApi.billing.builderCreditsPaymentIntent({
      params,
      body: {
        quantity,
        country: address.country,
        state: address.state,
        city: address.city,
        zip_code: address.zipCode,
        street: address.street,
        use_saved_card: useSavedCard,
        save_card: saveCard,
      },
    });
  },

  /**
   * Starts an Ordway account's AI-credits purchase, resolving as soon as the purchase workflow is
   * running. The charge outlives the request, so the returned `run_id` carries no outcome: callers
   * must poll it through {@link pollForOrdwayAICreditsPurchaseTask} before treating the purchase as
   * complete. A missing card, a non-card payment method, or a non-Ordway account throws instead —
   * those are refused before the workflow starts. A 409 means a *different* purchase is already in
   * flight and carries that run in its error `meta`; the same quantity arriving twice resolves with
   * the running run rather than starting a second one. Ports the Ember
   * `billing.createAICreditsOrdwayPurchase`.
   */
  async createAICreditsOrdwayPurchase(quantity: number): Promise<{ run_id: string }> {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot purchase AI credits without an account');
    }

    return hydraUiApi.billing.aiCreditsOrdwayPurchase({
      params: { path: { account_id: String(account.id) } },
      body: { quantity },
    });
  },

  /**
   * Starts an Ordway account's Inbox Previews pack purchase, resolving as soon as the purchase workflow
   * is running. `packSize` is the pack's preview count (100, 500, 2000 or 5000) — the server maps it to
   * a price, the same way the Stripe `payment_intent` body's `package` does.
   *
   * The charge outlives the request, so the returned `run_id` carries no outcome: callers must poll it
   * through {@link pollForOrdwayInboxPreviewsPurchaseTask} before treating the purchase as complete. A
   * missing card, a non-card payment method, a non-Ordway account, or an environment with no Ordway
   * previews product configured throws instead — all refused before the workflow starts. A 409 means a
   * *different* pack is already in flight and carries that run in its error `meta`; the same pack
   * arriving twice resolves with the running run rather than starting a second one.
   */
  async createInboxPreviewsOrdwayPurchase(packSize: number): Promise<{ run_id: string; status: string }> {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot purchase Inbox Previews without an account');
    }

    return hydraUiApi.billing.inboxPreviewsOrdwayPurchase({
      params: { path: { account_id: String(account.id) } },
      body: { package: packSize },
    });
  },

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot set a default payment method without an account');
    }

    await hydraUiApi.billing.paymentMethodDefault({
      params: { path: { account_id: String(account.id) } },
      body: { payment_method_id: paymentMethodId },
    });
  },

  /**
   * Loads the account usage into the `usage` atom. It is not part of `initialize`. Call it on demand, e.g.
   * with the `useBillingUsage` hook, so a page fetches usage only if it shows usage. Concurrent callers share
   * one load.
   */
  loadUsage: enqueueTask(
    async ({ refresh = false, silent = false }: { refresh?: boolean; silent?: boolean } = {}): Promise<void> => {
      const account = accountStore.get();
      if (account == null) {
        return;
      }

      // Report a failure to Sentry and show a toast. A caller cannot see the rejection, because it gets a
      // voided promise. `silent` hides only the toast, for a caller where usage is a secondary metric and a
      // failure must not interrupt the user, e.g. the profile-growth Total card. Sentry still gets the error.
      try {
        usage.set(await loadAccountUsage(account.id, refresh));
      } catch (error) {
        sentryCaptureException(error, { extra: { accountId: account.id } });
        if (!silent) {
          snackbarApi.create({
            variant: 'critical',
            id: 'billing.loadUsage.error',
            message: 'Could not load workspace usage. Try again.',
          });
        }
      }
    },
  ),

  /**
   * Loads the Ordway overage estimate into `ordwayBillingEstimate`, which
   * {@link billing.billingEstimates} reads. It runs only for an Ordway customer with an effective
   * subscription, and it skips a trial. On an error it reports to Sentry and shows a toast, as `loadUsage`
   * does.
   *
   * Concurrent callers share one load, which the Overview needs: three of its surfaces read
   * {@link useAllotmentUsage}, and each one asks for the estimate as it mounts. Coalesced rather than
   * queued because the request is an idempotent read — queuing would send it once per caller, one
   * after the other. The cost is that a caller arriving while a load is in flight reads the
   * subscription that load started with; the next call after it settles fetches again.
   */
  fetchOrdwayBillingEstimate: coalesceTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const sub = subscription.get();
    if (!isOrdwaySubscription(sub)) {
      return;
    }

    // A trial is free, so the app calculates its invoice and overage and does not fetch them.
    if (isTrial.get()) {
      return;
    }

    const subscriptionId = sub.id;
    if (subscriptionId == null) {
      return;
    }

    try {
      billing.setOrdwayBillingEstimate(await loadOrdwayBillingEstimate(account.id, String(subscriptionId)));
    } catch (error) {
      sentryCaptureException(error, { extra: { accountId: account.id } });
      snackbarApi.create({
        variant: 'critical',
        id: 'billing.fetchOrdwayBillingEstimate.error',
        message: 'Could not load your billing estimate. Try again.',
      });
    }
  }),

  /**
   * Loads the billing data of the current account into the atoms above.
   *
   * It is an enqueued task, so call `billing.initialize.perform(...)`. Concurrent callers share one load.
   * `perform` is a closure, so it stays callable after a destructure, and from the window global. The body
   * uses `billing`, and not `this`, because it runs outside the method context of the object.
   */
  initialize: enqueueTask(
    // fallow-ignore-next-line complexity -- inherent to the billing domain mapping
    async ({
      refresh = false,
      ignoreErrors = false,
      paramPlanSlug = '',
      includeCoupons = false,
    }: InitializeBillingOptions = {}): Promise<void> => {
      if (billing.initialized.get() && !refresh) {
        return;
      }

      // Stop a refresh that comes soon after the last one, because the user probably did not do an action
      // that needs new data. A plan param gives different data, so it always continues.
      const initializedAt = billing.initializedAt ?? 0;
      const recentlyInitialized = Date.now() - initializedAt <= RECENTLY_INITIALIZED_WINDOW_MS;

      if (billing.initialized.get() && refresh && paramPlanSlug === '' && recentlyInitialized) {
        return;
      }

      const account = accountStore.get();
      if (account == null) {
        return;
      }

      const isOrdway = billing.isOrdwayCustomer.get();
      const isMetronome = billing.isMetronomeVendor.get();

      // The Ordway profile endpoint calls the Ordway API live and fails for suspended, canceled,
      // or not-yet-provisioned accounts. The Metronome endpoint reads from our DB but 404s when no
      // contract exists yet (before the webhook lands). Both must degrade gracefully.
      const vendorIgnoreErrors =
        ignoreErrors ||
        account.account_state === AccountState.Suspended ||
        account.account_state === AccountState.Canceled ||
        billing.isOrdwayCustomerWaitingInitialization.get();

      const effectiveIgnoreErrors = isOrdway || isMetronome ? vendorIgnoreErrors : ignoreErrors;

      loadFailed.set(false);

      try {
        if (isMetronome) {
          const res = await loadMetronomeProfile(account.id, refresh);
          billing.setMetronomeBillingProperties(res);
        } else if (isOrdway) {
          const res = await loadOrdwayProfile(account.id, includeCoupons, refresh);
          billing.setOrdwayBillingProperties(res);
        } else {
          const res = await loadStripeInfo(account.id, refresh);
          billing.setStripeBillingProperties(res, paramPlanSlug);
        }

        billing.initialized.set(true);
        billing.initializedAt = Date.now();

        if (refresh) {
          // The billing state on the server can be different after a plan change or a new subscription. The
          // UI that the account controls, such as the nav gating and the home screen, reads the cached auth,
          // so refresh that too.
          //
          // Drop the cached account payloads first: the auth refetch the announcement triggers reads them
          // through `services/auth/account-api.ts`, whose entries are `cacheOnlyOnce` with a ~10s TTL, so
          // announcing over a warm cache hands every listener back the pre-change account. Same ordering,
          // and the same reason, as `invalidateAfterIdentifierMigration` and `reloadAfterRetryPayment`.
          //
          // Exact keys under `/accounts`, which also covers the billing profile reads cached to stay under
          // the vendor's rate limits; a prefix for `/account_by_environment`, which nothing else uses and
          // whose environment id is not in scope here. `useAuthQuery` picks between the two families by URL,
          // so both have to go.
          deleteCrossAppCacheKeys('/accounts/current', `/accounts/${account.id}`);
          deleteCrossAppCacheByPrefix('/account_by_environment');
          void announceRefreshAuth();
        }
      } catch (error) {
        loadFailed.set(true);
        if (effectiveIgnoreErrors) {
          billing.initialized.set(true);
          billing.initializedAt = Date.now();
          return;
        }
        throw error;
      }
    },
  ),
};

/** The public API of the billing service. */
export type BillingService = typeof billing;
